import { fensterAbfrageBeantwortet } from './lib/fensterabfrageAntwort';
import GuideViewControls from './components/GuideViewControls';
import GuideBuehneReiter, { GuideAnsichtKnopf, useGuideBuehneReiter } from './components/GuideBuehneReiter';
import type { BuehnenBezug } from '../shared/buehnen-anordnung';
import { kanzlerEingeklappt, setUiPreferences, useUiPreferences } from './lib/useUiPreferences';
import KlappKnopf from './components/KlappKnopf';
import { inspectNativeApp } from './lib/nativeAppEvidence';
import type { GuideBeraterStart } from './components/GuideBeraterChat';
import GuideBeraterSidebar from './components/GuideBeraterSidebar';
import { useState, useCallback, useEffect, useRef, useMemo, lazy, Suspense } from 'react';
import ProjectTabs from './components/ProjectTabs';
import { VersionBadge } from './components/VersionBadge';
import LayoutManager from './components/LayoutManager';
import StandalonePanel from './components/StandalonePanel';
import BuehnenVorschau from './components/BuehnenVorschau';
import LoginPage from './components/LoginPage';
import VariantPicker from './components/VariantPicker';
import { leseVarianteWahl, type GemerkteVariante } from './lib/varianteWahl';
import { ohneWaehlenParameter, torEntscheid, waehlenErzwungen } from './lib/variantePlan';
import SalesLanding from './components/SalesLanding';
import AreaPicker, { currentAreaChoice, pickArea } from './components/AreaPicker';
const MissionControl = lazy(() => import('./components/panels/MissionControl'));
const GroupChatPanel = lazy(() => import('./components/panels/GroupChatPanel'));
import AllChatsView from './components/AllChatsView';
import GuidePanel from './components/GuidePanel';
import { PersoenlicherAssistent } from './components/PersoenlicherAssistent';
import { GuideSchnellwahl } from './components/GuideSchnellwahl';
import { guideAnsichtSteuerung } from './lib/guideAnsichtSteuerung';
import PrivateGuideStatus from './components/PrivateGuideStatus';
/*
 * PartnerHof bleibt im Baum, wird hier aber nicht mehr gebraucht: seit dem 22.09.2026
 * bekommt ein Partner dasselbe Guide-Fenster wie Rafael (s. unten). Die Datei ist
 * absichtlich NICHT geloescht — ihr Hof-Ueberblick haengt an `/api/hof/meine-buehnen`,
 * der Route, die die Zugehoerigkeit misst, und dient weiter als kleine Ansicht ohne
 * Schaltpult (Handy, eingebettete Sicht).
 */
import { tippschutzAktiv } from './lib/entwuerfe';
import { Griff, CoachSpalte, FremdCoachSpalte } from './components/GuideFlaeche';
import GuideAnordnungSchalter, { ZweiterBildschirm, type ZweiterZustand } from './components/GuideAnordnungSchalter';
import { useGuideAnordnung, useBreitesFenster } from './lib/useGuideAnordnung';
import { useZweiterBildschirm } from './lib/useZweiterBildschirm';
import { ANLAUF_FRIST_MS, LEBENSZEICHEN_FRIST_MS, STEUER_MERKER, VOLLBILD_MERKER, VOLLBILD_PARAMETER, VOLLBILD_WERT, type AuftragsNachricht } from './lib/zweiterBildschirm';
import { GUIDE_ANORDNUNG_GRENZEN, GUIDE_SPALTE_MINDEST_PX, guideAnordnungSicht, type GuideAnordnungModus } from '../shared/guide-anordnung';

import CuiLitePanel from './components/panels/CuiLitePanel';
import HintergrundBrowser from './components/HintergrundBrowser';
import {
  acquireScreenId,
  persistScreenChoice,
  publishScreenClaim,
  releaseScreenClaim,
  isScreenId,
  HEARTBEAT_MS,
  forgetInheritedClaim,
} from './lib/screenClaim';
import type { Project } from './types';
import { useSessionStore } from './contexts/SessionStore';
import { useAuth, type AuthUser } from './contexts/AuthContext';
import { useAngeheftet, setzeAngeheftet, leseAngeheftet, gehoertZurAngehefteten } from './lib/buehnenAnheften';
import { useBuehneHalten, halteAmServer } from './lib/useBuehneHalten';
import { guideAnfangsZustand, istPartnerAnsicht, merkeGuideWahl } from './lib/guideVorgabe';
import { buehnenWirt } from './lib/buehnenWirt';
import { IS_LOCAL } from './env';
import { isSubSession } from './utils/subSession';
import { isOpenChat, isOrphanedChat, resolveProjectId, reportUnattributableOpenChats } from './utils/chatLifecycle';
import { conversationsUrl } from './utils/conversationScope';
import { aufnahmeHintergrund, farbenFuerHtml2canvas } from './lib/aufnahmeFarben';
import { istBrowserPanelGemountet } from './lib/gemounteteBrowserPanels';
import { buehneNichtGezeichnet } from './lib/guideOeffnungsBeleg';
import { rafaelNavStand } from './lib/rafaelNavigation';
import { b2AuftragAusfuehren, fensterZusatz, missionAbdeckungRaeumen, vermerkAusQuittung } from './lib/zweiterBildschirmVerdrahtung';
declare const __BUILD_TIME__: string;

/** Hinweis auf Bildschirm 1, wenn Bildschirm 2 weg ist (25.09.2026) — geht nach 15 s von selbst. */
const B2_WEG_HINWEIS = 'Bildschirm 2 wurde geschlossen — Bühnen gehen wieder hier auf. „Zweiten Bildschirm öffnen" koppelt neu.';

const API = '/api';

const DEFAULT_PROJECTS: Project[] = [];

async function fetchProjects(): Promise<Project[]> {
  const res = await fetch(`${API}/projects`, { signal: AbortSignal.timeout(5000) });
  if (!res.ok) throw new Error('Failed to load projects');
  const projects = await res.json();
  return projects.length > 0 ? projects : DEFAULT_PROJECTS;
}

async function saveProject(project: Project): Promise<void> {
  if (window.__cuiServerAlive === false) return;
  try {
    await fetch(`${API}/projects`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(project),
      signal: AbortSignal.timeout(20000),
    });
  } catch (err) {
    console.warn('[App] saveProject failed:', err);
  }
}

async function deleteProject(id: string): Promise<void> {
  if (window.__cuiServerAlive === false) return;
  try {
    await fetch(`${API}/projects/${id}`, { method: 'DELETE', signal: AbortSignal.timeout(20000) });
  } catch (err) {
    console.warn('[App] deleteProject failed:', err);
  }
}

// --- Project Dialog (create + edit) ---
interface ProjectDialogProps {
  mode: 'create' | 'edit';
  initialName?: string;
  initialWorkDir?: string;
  onSubmit: (name: string, workDir: string) => void;
  onClose: () => void;
}

function ProjectDialog({ mode, initialName = '', initialWorkDir = '', onSubmit, onClose }: ProjectDialogProps) {
  const [name, setName] = useState(initialName);
  const [workDir, setWorkDir] = useState(initialWorkDir);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => { nameRef.current?.focus(); nameRef.current?.select(); }, []);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    // Local mode: user provides workDir; Remote: server auto-creates workspace
    onSubmit(name.trim(), (mode === 'edit' || IS_LOCAL) ? workDir.trim() : '');
  }

  const isEdit = mode === 'edit';
  const title = isEdit ? 'Projekt bearbeiten' : 'Neues Projekt';
  const submitLabel = isEdit ? 'Speichern' : 'Erstellen';

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--tn-surface)', border: '1px solid var(--tn-border)',
          borderRadius: 8, padding: 20, width: 380,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--tn-text)', marginBottom: 16 }}>
          {title}
        </div>

        <label style={{ fontSize: 11, color: 'var(--tn-text-muted)', display: 'block', marginBottom: 4 }}>
          Projektname
        </label>
        <input
          ref={nameRef}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="z.B. RLB Campus"
          style={{
            width: '100%', padding: '6px 10px', fontSize: 12,
            background: 'var(--tn-bg)', color: 'var(--tn-text)',
            border: '1px solid var(--tn-border)', borderRadius: 4,
            marginBottom: 12, boxSizing: 'border-box',
          }}
        />

        {(isEdit || IS_LOCAL) && (
          <>
            <label style={{ fontSize: 11, color: 'var(--tn-text-muted)', display: 'block', marginBottom: 4 }}>
              Arbeitsverzeichnis (absoluter Pfad)
            </label>
            <input
              value={workDir}
              onChange={(e) => setWorkDir(e.target.value)}
              placeholder={IS_LOCAL ? '/Users/rafael/Documents/...' : '/root/projekte/...'}
              style={{
                width: '100%', padding: '6px 10px', fontSize: 12,
                background: 'var(--tn-bg)', color: 'var(--tn-text)',
                border: '1px solid var(--tn-border)', borderRadius: 4,
                marginBottom: 16, boxSizing: 'border-box',
              }}
            />
          </>
        )}
        {!isEdit && !IS_LOCAL && (
          <div style={{ fontSize: 10, color: 'var(--tn-text-muted)', marginBottom: 16, opacity: 0.7 }}>
            Workspace wird automatisch erstellt
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '6px 14px', borderRadius: 5, fontSize: 12, cursor: 'pointer',
              background: 'var(--tn-bg)', border: '1px solid var(--tn-border)', color: 'var(--tn-text-muted)',
            }}
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={!name.trim()}
            style={{
              padding: '6px 16px', borderRadius: 5, fontSize: 12, cursor: 'pointer', fontWeight: 600,
              background: name.trim() ? 'var(--tn-blue)' : 'var(--tn-border)',
              border: 'none', color: '#fff',
              opacity: name.trim() ? 1 : 0.5,
            }}
          >
            {submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

// --- Delete Confirm Dialog ---
function DeleteDialog({ projectName, onConfirm, onClose }: { projectName: string; onConfirm: () => void; onClose: () => void }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--tn-surface)', border: '1px solid var(--tn-border)',
        borderRadius: 8, padding: 20, width: 320,
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      }}>
        <div style={{ fontSize: 13, color: 'var(--tn-text)', marginBottom: 16 }}>
          Projekt <strong>{projectName}</strong> wirklich loeschen?
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            onClick={onClose}
            style={{
              padding: '6px 14px', borderRadius: 5, fontSize: 12, cursor: 'pointer',
              background: 'var(--tn-bg)', border: '1px solid var(--tn-border)', color: 'var(--tn-text-muted)',
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: '6px 16px', borderRadius: 5, fontSize: 12, cursor: 'pointer', fontWeight: 600,
              background: 'var(--tn-red)', border: 'none', color: '#fff',
            }}
          >
            Loeschen
          </button>
        </div>
      </div>
    </div>
  );
}

/** A user must pick a variant when they have more than one AND are currently
 *  on a shared (Standard-CUI) instance. Inside an own variant the choice is
 *  already made; users without variants never see the picker. */
function needsVariantPick(user: AuthUser | null, currentPort: number | null): boolean {
  const variants = user?.variants;
  if (!variants || variants.length <= 1 || currentPort == null) return false;
  return variants.find(v => v.port === currentPort)?.shared === true;
}

// Idle-eviction TTL for headless browser mounts (see mountedLastUsed below).
// A project mounted only to drive its browser headlessly is unmounted after
// this much INACTIVITY to free its LayoutManager + <webview> RAM.
//
// Must stay comfortably above the server's maximum command-ack window
// (MAX_ACK_TIMEOUT_MS in server/routes/browser-console.ts, 5 min): the
// sweep runs on wall-clock idle time, so a TTL equal to the ack cap could
// evict a workspace WHILE its longest legal eval is still running — pulling
// the <webview> out from under a live upload. 10 min leaves a full ack
// window of slack while keeping the mount bounded.
const MOUNT_IDLE_TTL_MS = 10 * 60 * 1000;

export default function App() {
  const { authenticated, authEnabled, user, currentPort } = useAuth();
  const [variantChosen, setVariantChosen] = useState(() => {
    try { return sessionStorage.getItem('cui-variant-picked') === '1'; } catch { return false; }
  });
  /* Variantenwahl je Konto, am Server gemerkt (lib/varianteWahl.ts, WIRKBELEG Partner 8a6548d7b5):
   * sessionStorage allein liess die Wahl bei jedem neuen Fenster bzw. Kachel-Neuaufbau verfallen. */
  const pickNoetig = authenticated === true && needsVariantPick(user, currentPort);
  /* Dauerhafter Rueckweg (Zweitpruefung R1, B2): `?variante=waehlen` zeigt den Picker auf jeder
   * Instanz des Kontos, ohne automatisches Oeffnen (Kopfzeile: Knopf „Variante wechseln"). */
  const [waehlenModus, setWaehlenModus] = useState(() => waehlenErzwungen(window.location.search));
  const [gemerkt, setGemerkt] = useState<GemerkteVariante | null>(null);
  const [varianteHinweis, setVarianteHinweis] = useState('');
  useEffect(() => {
    if (!pickNoetig || variantChosen || !user?.id) return;
    let ab = false;
    void leseVarianteWahl(user.id).then(g => {
      if (ab) return;
      setGemerkt(g);
      if (g.fehler) setVarianteHinweis(g.quelle === 'lokal' ? `${g.fehler} Verwendet wird die in diesem Browser gemerkte Wahl.` : g.fehler);
    });
    return () => { ab = true; };
  }, [pickNoetig, variantChosen, user?.id]);

  // Auth gate: show login page when auth is enabled and user isn't authenticated
  if (authEnabled && authenticated === false) {
    return <LoginPage />;
  }

  // Still checking auth status — show nothing (prevents flash)
  if (authenticated === null) {
    return <div style={{ background: 'var(--tn-bg)', height: '100dvh' }} />;
  }


  // Deep-link mode: /?panel=<value> renders ONE global panel fullscreen, skipping
  // the project/variant shell. Stays behind the auth gate above (admin panels are
  // sensitive). Purpose: a stable URL per panel that can be loaded in a browser and
  // driven via the /api/browser-panel control API — see StandalonePanel.tsx.
  const deepLinkPanel = new URLSearchParams(window.location.search).get('panel');
  if (deepLinkPanel) {
    // Der Guide gehoert dem Eigentuemer (Rafael, 09.09.2026 13:2xZ: "meine buehnen
    // sind mein eigentum - das muss an den user gebunden sein"). Der Server sperrt
    // /api/guide ohnehin (403) — hier zusaetzlich, damit kein leeres Panel entsteht.
    if (deepLinkPanel === 'guide' && authEnabled && user?.role !== 'admin') {
      return <PrivateGuideStatus />;
    }
    return <StandalonePanel value={deepLinkPanel} />;
  }

  /*
   * Deep-Link-Modus: /?buehne=<karteId> zeigt NUR die Buehne dieser einen
   * Guide-Karte — fuer den Selbst-Screenshot einer Session, BEVOR sie eine
   * Entscheidung als bereit meldet (server/routes/screenshots.ts:
   * /api/capture/buehne). Admin-only wie die Karten selbst: sie tragen Rafaels
   * Entscheidungen. Siehe BuehnenVorschau.tsx fuer das Warum der eigenen Seite.
   */
  const deepLinkBuehne = new URLSearchParams(window.location.search).get('buehne');
  if (deepLinkBuehne) {
    /* Partner (KD 91624155, 01.11.2026): nur die EIGENE Buehne, und nur als Buehnen-Objekt
     * (`buehne:<id>`; eine nackte Kennung wird so gelesen). Karten und Themen tragen Rafaels
     * Entscheidungen und bleiben Admin-Sache. Die Freigabe entscheidet der Server
     * (partner-guide-tor.ts: Eigentum; geteilte Buehnen sind fuer die Vorschau gesperrt) —
     * ein 403 zeigt BuehnenVorschau als „Diese Bühne gehört nicht zu deinem Konto". */
    if (authEnabled && user?.role !== 'admin') {
      const id = deepLinkBuehne.startsWith('buehne:') ? deepLinkBuehne.slice('buehne:'.length) : deepLinkBuehne;
      return <BuehnenVorschau karteId={`buehne:${id}`} partnerSicht />;
    }
    return <BuehnenVorschau karteId={deepLinkBuehne} />;
  }

  // Partners with uiMode='sales' get BOTH the full Arbeitsbereich and the
  // simplified Sales-Landing — never one at the exclusion of the other. First
  // visit this tab: show the picker. After that: honor the remembered choice.
  // (uiMode marks who gets the *option*, not which one they're stuck with.)
  if (user?.uiMode === 'sales') {
    const areaChoice = currentAreaChoice();
    if (!areaChoice) {
      return <AreaPicker />;
    }
    if (areaChoice === 'sales') {
      return <SalesLanding />;
    }
    // areaChoice === 'work' → fall through to the normal workspace below.
  }

  // Variant gate: more than one variant + currently on the shared Standard-CUI.
  if (authenticated === true && (waehlenModus || (!variantChosen && pickNoetig))) {
    // Gemerkte Wahl wird noch gelesen — kurz nichts zeigen statt den Picker aufblitzen zu lassen.
    if (!waehlenModus && !gemerkt) return <div style={{ background: 'var(--tn-bg)', height: '100dvh' }} />;
    const entscheid = torEntscheid({ varianten: user?.variants ?? [], currentPort, gemerkterPort: gemerkt?.port ?? null, erzwungen: waehlenModus, schonGewaehlt: variantChosen });
    if (entscheid.art === 'picker') {
      return <VariantPicker key={waehlenModus ? 'waehlen' : 'auto'} autoPort={entscheid.autoPort} hinweis={varianteHinweis} onStay={(fehler) => {
        try { sessionStorage.setItem('cui-variant-picked', '1'); } catch { /* silent-ok: sessionStorage nur Beschleuniger */ }
        // `?variante=waehlen` nach der Wahl entfernen, damit ein Neuladen nicht wieder fragt.
        try { window.history.replaceState(null, '', window.location.pathname + ohneWaehlenParameter(window.location.search) + window.location.hash); } catch { /* silent-ok: nur die Adresszeile, die Wahl gilt ueber State */ }
        if (fehler) setVarianteHinweis(fehler);
        setWaehlenModus(false);
        setVariantChosen(true);
      }} />;
    }
  }

  return (
    <>
      {varianteHinweis && (
        <div data-variante-hinweis role="status" style={{ position: 'fixed', left: 12, bottom: 12, zIndex: 10000, maxWidth: 420, padding: '8px 12px', borderRadius: 6, fontSize: 12, background: 'var(--tn-bg-dark, #1a1b26)', color: 'var(--tn-orange, #e0a040)', border: '1px solid var(--tn-border)' }}>
          {varianteHinweis}{' '}
          <button onClick={() => setVarianteHinweis('')} style={{ marginLeft: 6, background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', textDecoration: 'underline', fontSize: 12 }}>Schließen</button>
        </div>
      )}
      <AppContent />
    </>
  );
}

function AppContent() {
  const { user, authEnabled } = useAuth();
  /* Betriebsteile des Guide gehoeren dem Eigentuemer; ein Partner sieht sie nicht
   * (Rafael, 22.09.2026: „Weglassen, was ihm nicht gehoert"). */
  const istBetreiberGuide = !authEnabled || user?.role === 'admin';
  const guideSteuerungErlaubt = useRef(false);
  /* Letzte Fokus-/Eingabe-Aktivitaet dieses Fensters: danach waehlt der Server bei
   * mehreren Fenstern ohne Fokus das zuletzt aktive (server/lib/guide-oeffnung.ts). */
  const zuletztAktiv = useRef(Date.now());
  useEffect(() => {
    const merken = () => { zuletztAktiv.current = Date.now(); };
    window.addEventListener('focus', merken);
    window.addEventListener('pointerdown', merken, true);
    window.addEventListener('keydown', merken, true);
    return () => {
      window.removeEventListener('focus', merken);
      window.removeEventListener('pointerdown', merken, true);
      window.removeEventListener('keydown', merken, true);
    };
  }, []);
  guideSteuerungErlaubt.current = authEnabled === false || user?.role === 'admin';
  // Self-service UI scale (David Steiner feature request, 04.08.2026): applied
  // here via CSS zoom rather than a font-size/rem retrofit because most panels
  // use hardcoded px inline styles. zoom (unlike transform) reflows layout, so
  // getBoundingClientRect()-based click/screenshot coordinates downstream stay
  // correct. Reset to 1 on every `data-workspace-root` below and again per-panel
  // in LayoutManager's wrapPanel for the Browser panel specifically — an
  // Electron <webview> does not tolerate zoom/transform on ANY ancestor (see
  // BrowserPanel.tsx comment on the device-frame), so the scale must never
  // reach one.
  const [projects, setProjects] = useState<Project[]>([]);
  // screenId is per-window (sessionStorage), not shared between browser windows.
  // Screen 2 gets a separate layout key so it never overwrites Screen 1's layout.
  // acquireScreenId coordinates across windows: a brand-new window claims the lowest
  // FREE screen (so a second window auto-lands on B2 instead of colliding on B1),
  // while a reloaded window keeps its sticky choice. See lib/screenClaim.ts.
  /*
   * BUEHNEN-VOLLBILD (Bildschirm 2, Rafael 25.09.2026): ein Fenster, das
   * „Zweiten Bildschirm öffnen" aufgemacht hat. Es ist immer B2, zeigt keine
   * Guide-Spalte und oeffnet jede Buehne, die Bildschirm 1 hinueberreicht.
   * Der Merker in sessionStorage laesst ein Neuladen in diesem Modus.
   */
  const b2VollbildRef = useRef(false);
  const [b2Vollbild, setB2Vollbild] = useState(() => {
    const ausUrl = new URLSearchParams(window.location.search).get(VOLLBILD_PARAMETER) === VOLLBILD_WERT;
    try {
      if (ausUrl && sessionStorage.getItem(VOLLBILD_MERKER) !== '1') {
        forgetInheritedClaim();                    // geerbt vom Oeffner, siehe screenClaim.ts
        sessionStorage.removeItem(STEUER_MERKER);  // ebenso geerbt: dieses Fenster steuert nichts
        sessionStorage.setItem(VOLLBILD_MERKER, '1');
      }
      return ausUrl || sessionStorage.getItem(VOLLBILD_MERKER) === '1';
    } catch { return ausUrl; }
  });
  b2VollbildRef.current = b2Vollbild;
  const [screenId, _setScreenId] = useState(() => {
    if (b2Vollbild) { persistScreenChoice('2'); return '2' as const; }
    return acquireScreenId();
  });
  const setScreenId = useCallback((id: string) => {
    if (!isScreenId(id)) { console.error('[App] ignoring invalid screenId:', id); return; }
    _setScreenId(id);
    persistScreenChoice(id); // sticky for this window + sync the cross-window registry
  }, []);
  // Same per-window suffix pattern as layoutKey below (p.id--s${screenId}). Without it,
  // this key is a single localStorage slot shared by every window: B2 switching its
  // workspace silently overwrites what B1 falls back to on its own next reload (e.g.
  // the auto-reload checkForUpdate triggers after a deploy) — B1 wakes up on whatever
  // project B2 last had active, no user action on B1 involved. Reported 2026-08-04:
  // "Werking Report" view replaced by "Werking Energy Report" out of nowhere.
  const activeProjectKey = screenId !== '1' ? `cui-active-project--s${screenId}` : 'cui-active-project';
  const [activeId, _setActiveId] = useState(() => {
    try { return localStorage.getItem(activeProjectKey) || ''; } catch (err) { console.warn('[App] localStorage activeId read failed:', err); return ''; }
  });
  // Keep this window's screen claim alive so other windows see B1/B2 as occupied, and
  // release it on close so the slot frees up. Stale claims (crashed windows) self-prune.
  const screenIdRef = useRef(screenId);
  screenIdRef.current = screenId;
  useEffect(() => {
    publishScreenClaim(screenIdRef.current);
    const heartbeat = window.setInterval(() => publishScreenClaim(screenIdRef.current), HEARTBEAT_MS);
    const release = () => releaseScreenClaim();
    window.addEventListener('pagehide', release);
    return () => {
      window.clearInterval(heartbeat);
      window.removeEventListener('pagehide', release);
      releaseScreenClaim();
    };
  }, []);
  const setActiveId = useCallback((id: string) => {
    _setActiveId(id);
    try { localStorage.setItem(activeProjectKey, id); } catch (err) { console.warn('[App] localStorage setActiveId write failed:', err); }
  }, [activeProjectKey]);
  const [loaded, setLoaded] = useState(false);
  const [mounted, setMounted] = useState<Set<string>>(new Set());
  // Guide-Spalte (GD): steht LINKS NEBEN dem echten Workspace, nicht darin. Der
  // Workspace-Container wird nur schmaler — kein zweites Rendern der <webview>-Panels.
  /*
   * DER GUIDE UEBERLEBT EIN NEULADEN.
   *
   * Rafael, 06.09. 16:56Z, mit Bild: "Oft bin ich mitten im Coach, dann resetet er
   * und schickt mich auf die normale Workspace-Ansicht zurueck, die ich gar nicht
   * haben will." Ursache: nach jedem Push baut cui-auto-pull das Frontend neu, die
   * Oberflaeche laedt sich neu — und startete danach im zuletzt aktiven
   * Arbeitsbereich, weil der Guide-Modus nirgends stand. Jeder unserer eigenen
   * Pushes warf ihn also aus seiner Ansicht.
   *
   * Der Wert wird schon im Initialisierer gelesen, nicht in einem Effekt: sonst
   * zeichnet die App einen Bildschirm lang den Arbeitsbereich, bevor der Guide
   * zurueckkommt, und genau dieses Aufblitzen ist das, was er als "reset" sieht.
   */
  /*
   * FERN-ANSICHT (`?dgr=1`) — so sieht dieses Haus aus, wenn das ANDERE es zeigt.
   *
   * Rafael, 08.09. 17:39Z: "aber er zeigt die buehnen trotzdem doppelt? das
   * macht ja keinen sinn … also den guide zeigt er doppelt, rechts gehoert ja
   * die buehne hin." Der erste Anlauf lud drueben nur das Guide-Panel
   * (?panel=guide) — dann stand links sein Guide und rechts noch einer, und die
   * Buehne fehlte ganz.
   *
   * Diese Ansicht ist deshalb der ganze Guide-Modus OHNE Kopfzeile: links die
   * Themen und Karten dieses Hauses, rechts die Buehne, also die Fenster, die
   * die zustaendige Sitzung hier gesetzt hat. Keine zweite Kopfzeile, keine
   * Arbeitsbereichs-Reiter, keine Kontenleiste — die gehoeren dem Haus, in dem
   * Rafael sitzt, und doppelt waeren sie eine Einladung, sich zu verirren.
   */
  const fernAnsicht = new URLSearchParams(window.location.search).get('dgr') === '1';
  const [guideOeffnungsAuftrag, setGuideOeffnungsAuftrag] = useState<{
    clientId: string; buehneId: string; auftragId: string; expiresAt: number; handNav: number; nurAngebot?: boolean;
  } | null>(null);
  /* Fuer einen Partner ist der Guide standardmaessig OFFEN, und gemerkt wird je
   * Nutzer nur seine bewusste Wahl — Regel und Begruendung in lib/guideVorgabe.ts.
   * Fuer Rafael (Admin) und ohne Anmeldung gilt unveraendert der alte Merker. */
  const partnerGuide = istPartnerAnsicht(authEnabled, user);
  const [showGuide, setShowGuide] = useState(() => !b2Vollbild && (new URLSearchParams(location.search).has('guide-buehne') || guideAnfangsZustand({
    fernAnsicht, authEnabled, user,
    touchInput: navigator.maxTouchPoints > 0 || window.matchMedia("(pointer: coarse)").matches,
    speicher: typeof localStorage === 'undefined' ? null : localStorage,
  })));
  /** Ein bewusster Klick (Knopf oben oder „schliessen" im Guide) — beim Partner je Nutzer gemerkt. */
  const guideBewusstSetzen = useCallback((offen: boolean) => {
    setShowGuide(offen);
    merkeGuideWahl({ authEnabled, user, offen, speicher: typeof localStorage === 'undefined' ? null : localStorage });
  }, [authEnabled, user]);
  // In der Fern-Ansicht NICHT merken: sie ist eine Sicht von aussen, kein
  // Umschalten dieses Hauses — sonst faende Rafael hier spaeter einen Zustand vor,
  // den er nie gewaehlt hat. Beim Partner ebenso nicht: dort merkt nur
  // `guideBewusstSetzen` (sonst fror der Anfangszustand als seine „Wahl" ein).
  // Das Buehnen-Vollbild merkt ebenfalls nichts: es hat den Guide nie abgewaehlt.
  useEffect(() => { if (!fernAnsicht && !partnerGuide && !b2Vollbild) localStorage.setItem('guide-modus', showGuide ? 'an' : 'aus'); }, [showGuide, fernAnsicht, partnerGuide, b2Vollbild]);

  /*
   * EIN NEULADEN MITTEN IN EINER KARTE WIRD VERSCHOBEN, NICHT VERWORFEN.
   *
   * Liest oder beantwortet Rafael gerade eine Entscheidung, ist ein Reload das
   * Schlimmste, was passieren kann: der getippte Satz ist weg. Deshalb merkt sich
   * die App den Wunsch und fuehrt ihn aus, sobald er die Karte verlaesst.
   */
  const arbeitetAnKarte = useRef(false);
  const reloadOffen = useRef<string | null>(null);
  const neuLadenWennMoeglich = useCallback((grund: string) => {
    if (arbeitetAnKarte.current) {
      console.log(`[App] Neuladen (${grund}) verschoben — Rafael ist gerade an einer Karte.`);
      reloadOffen.current = grund;
      return;
    }
    /*
     * ...UND AUCH NICHT MITTEN IM SATZ (Rafael, 07.09. 18:47Z).
     *
     * "ich tippe was, dann kommt der Rebuild, und der getippte Text ist weg."
     * Gemessen: 18:45:03Z Build, 18:45:47Z Reload an alle Clients, waehrend er
     * tippte. `arbeitetAnKarte` deckte nur die Karte ab — ein halb getippter
     * Chat-Satz zaehlte nicht.
     *
     * Der Text selbst ist inzwischen gesichert (lib/entwuerfe), das Warten
     * schuetzt also den GEDANKEN, nicht die Zeichen. Deshalb endet es nach
     * TIPP_SCHUTZ_MS ohne Tastendruck von selbst: ein vergessener Entwurf darf
     * das Neuladen nicht ewig blockieren — und kostet dann auch nichts mehr.
     */
    if (tippschutzAktiv()) {
      console.log(`[App] Neuladen (${grund}) verschoben — irgendwo steht ungesendeter Text.`);
      reloadOffen.current = grund;
      return;
    }
    window.location.reload();
  }, []);

  /*
   * Wird das Feld leer (gesendet oder geloescht), laeuft ein verschobenes
   * Neuladen sofort — und spaetestens, wenn der Tippschutz auslaeuft. Ohne diesen
   * zweiten Weg bliebe ein Reload haengen, bis Rafael zufaellig wieder etwas tippt.
   */
  useEffect(() => {
    const pruefen = () => {
      if (!reloadOffen.current || arbeitetAnKarte.current || tippschutzAktiv()) return;
      const grund = reloadOffen.current;
      reloadOffen.current = null;
      console.log(`[App] Feld leer — verschobenes Neuladen (${grund}) wird jetzt ausgefuehrt.`);
      window.location.reload();
    };
    window.addEventListener('cui-entwuerfe', pruefen);
    const uhr = setInterval(pruefen, 30_000);
    return () => { window.removeEventListener('cui-entwuerfe', pruefen); clearInterval(uhr); };
  }, []);
  const handleGuideArbeit = useCallback((aktiv: boolean) => {
    arbeitetAnKarte.current = aktiv;
    if (!aktiv && reloadOffen.current) {
      const grund = reloadOffen.current;
      reloadOffen.current = null;
      console.log(`[App] Karte verlassen — verschobenes Neuladen (${grund}) wird jetzt ausgefuehrt.`);
      window.location.reload();
    }
  }, []);

  /**
   * Der Umbau-Vorhang ueber dem Arbeitsbereich. Die Guide-Spalte meldet ihn per
   * Fensterereignis an und ab — bewusst ohne Context: der Vorhang haengt an EINER
   * Stelle im Baum, und ein Ereignis kostet keine Neuzeichnung der ganzen App.
   */
  const [umbau, setUmbau] = useState<{ aktiv: boolean; was: string }>({ aktiv: false, was: '' });
  /** Der Guide steht ohne gewaehltes Thema da — dann gehoert ihm die Flaeche. */
  const [guideBuehne, setGuideBuehne] = useState(false);
  /*
   * Das Fenster der gerade offenen Entscheidung, als flexlayout-Dokument, samt dem
   * Arbeitsbereich, ueber dem es liegt. Nicht gespeichert und nicht geteilt: es
   * entsteht beim Oeffnen einer Karte und verschwindet mit ihr.
   */
  const [beraterStart, setBeraterStart] = useState<GuideBeraterStart | null>(null);
  const beraterBuehneId = beraterStart?.buehne.id ?? null;
  const [beraterChatOffen, setBeraterChatOffen] = useState(false);
  useEffect(() => { setBeraterChatOffen(false); }, [beraterBuehneId]);
  const [buehnenLayout, setBuehnenLayout] = useState<{ doc: unknown; projectId: string; bezug?: BuehnenBezug } | null>(null);
  /*
   * Wer die Bühnenschicht montiert. Bis 01.10.2026 nur der LayoutManager mit
   * `p.id === buehnenLayout.projectId` — fehlte der Arbeitsbereich im Haus (Partner-
   * Bühnen aus energy-entwicklung auf Dev), montierte sie niemand, still.
   */
  /*
   * Die angeheftete Buehne (lib/buehnenAnheften.ts) wohnt im Arbeitsbereich, in dem sie
   * angeheftet wurde — eingefroren, NICHT nach `activeId` nachgefuehrt: zoege sie beim
   * Wechsel in einen anderen LayoutManager um, waere sie abgebaut und neu montiert.
   * Fehlt ihr Arbeitsbereich (inzwischen geloescht), nimmt sie den ersten vorhandenen —
   * ebenfalls ohne `activeId`, damit der Wirt stabil bleibt.
   */
  const anheftNutzer = user?.id ?? 'local';
  const angeheftet = useAngeheftet(anheftNutzer);
  const angeheftetWirtId = useMemo(
    () => angeheftet ? buehnenWirt(angeheftet.projectId, projects.map(p => p.id), null).projectId : null,
    [angeheftet, projects],
  );
  const angeheftetWirtRef = useRef<string | null>(null);
  angeheftetWirtRef.current = angeheftetWirtId;
  const buehnenWirtId = useMemo(
    () => !buehnenLayout ? null
      : angeheftetWirtId && gehoertZurAngehefteten(angeheftet, buehnenLayout.bezug?.id) ? angeheftetWirtId
      : buehnenWirt(buehnenLayout.projectId, projects.map(p => p.id), activeId).projectId,
    [buehnenLayout, projects, activeId, angeheftet, angeheftetWirtId],
  );
  const buehnenLayoutRef = useRef(buehnenLayout); buehnenLayoutRef.current = buehnenLayout;
  const buehnenWirtRef = useRef(buehnenWirtId); buehnenWirtRef.current = buehnenWirtId;
  /** Wirft bei vollem/gesperrtem Geraetespeicher — der Knopf an der Buehne zeigt das an. */
  /*
   * Der Knopf meldet den Halt auch am Server (routes/buehne-halten.ts): Knopf und API sind
   * dieselbe Wahrheit, und ein Berater sieht per GET, ob seine Buehne gehalten wird.
   */
  const handleAnheften = useCallback((id: string, an: boolean) => {
    if (!an) {
      if (leseAngeheftet(anheftNutzer)?.id === id) {
        setzeAngeheftet(anheftNutzer, null);
        halteAmServer(null, 'rafael-knopf').catch(e => console.error('[buehne-halten] Lösen am Server fehlgeschlagen:', e));
      }
      return;
    }
    const offen = buehnenLayoutRef.current;
    const wirt = buehnenWirtRef.current;
    if (!offen?.bezug || offen.bezug.id !== id || !wirt) {
      throw new Error(`Nur die gerade gezeigte Bühne lässt sich anheften (gezeigt: ${offen?.bezug?.id ?? 'keine'}).`);
    }
    setzeAngeheftet(anheftNutzer, { id, projectId: wirt, doc: offen.doc, bezug: offen.bezug, seit: Date.now(), quelle: 'server', kette: [id] });
    halteAmServer(id, 'rafael-knopf').catch(e => console.error('[buehne-halten] Halten am Server fehlgeschlagen:', e));
  }, [anheftNutzer]);
  const handleBuehnenLayout = useCallback((doc: unknown | null, projectId: string | null, bezug?: BuehnenBezug) => {
    setBuehnenLayout(doc && projectId ? { doc, projectId, bezug } : null);
  }, []);
  // Stabil, sonst laeuft der Abmelde-Effekt im Guide bei jedem Rendern.
  const [guideBuehneGrund, setGuideBuehneGrund] = useState<string | null>(null);
  const handleGuideBuehne = useCallback((aktiv: boolean, grund?: string | null) => {
    setGuideBuehne(aktiv);
    setGuideBuehneGrund(grund ?? null);
  }, []);
  const ui = useUiPreferences();
  const guideHidden = !ui.focus && (ui.guideCollapsed ?? (ui.narrow && !guideBuehne && !!beraterBuehneId));
  const [coachTab, setCoachTab] = useState<'own' | 'other'>('own');
  /* Anordnung der Spalte je Person, am Server gemerkt (Rafael, 25.09.2026) — lib/useGuideAnordnung.ts. */
  const anordnungPerson = authEnabled ? (user?.id ?? 'unbekannt') : 'ohne-anmeldung';
  const { stand: anordnung, aendern: anordnungAendern, fehler: anordnungFehler } = useGuideAnordnung(anordnungPerson);
  const breitesFenster = useBreitesFenster();
  /* Bildschirm 1 steuert einen zweiten Bildschirm — je Fenster, ueberlebt ein Neuladen. */
  const [b1Steuert, setB1Steuert] = useState(() => { try { return !b2Vollbild && sessionStorage.getItem(STEUER_MERKER) === '1'; } catch { return false; } });
  /*
   * Welcher Chat rechts steht. Heute der Coach; sobald eine Karte offen ist, soll
   * es der Chat sein, DER DIE SEITE GEBAUT HAT (Rafael, 06.09.) — dafuer fehlt
   * noch das Feld an der Karte, deshalb steht hier bewusst nur der Coach und
   * nicht ein geratener Ersatz.
   */
  const [coachChat, setCoachChat] = useState<{ sessionId: string; accountId?: string } | null>(null);
  const [coachTitel, setCoachTitel] = useState('Coach');
  const [coachGeladen, setCoachGeladen] = useState(false);
  const [coachFehler, setCoachFehler] = useState<string | null>(null);
  const [coachVersuch, setCoachVersuch] = useState(0);
  const [eigenesHaus, setEigenesHaus] = useState<'dev' | 'partner' | null>(null);
  /*
   * SEIN KANZLER FUER EINEN PARTNER (Rafael, 23.09.2026: „Der Kanzler ist ja
   * unabhaengig von jedem User. Sobald man in den Dev-Modus reingeht, hat man auch
   * einen Kanzler."). Dieselbe Spalte wie Rafaels Coach, nur die Quelle ist seine:
   * `/api/guide/mein-kanzler` statt der Hauskonfiguration. Fehlt er, wird er beim
   * Betreten gestartet — kein Knopf, keine Buehnenwahl davor.
   */
  useEffect(() => {
    if (!showGuide || authEnabled !== true || user?.role === 'admin') return;
    let abgebrochen = false;
    setCoachGeladen(false);
    (async () => {
      const r = await fetch('/api/guide/mein-kanzler', { signal: AbortSignal.timeout(20_000) });
      const d = await r.json().catch(() => null) as { chat?: { sessionId: string; accountId?: string } | null; error?: string } | null;
      if (!r.ok) throw new Error(d?.error || `HTTP ${r.status}`);
      if (d?.chat) return d.chat;
      const s = await fetch('/api/guide/mein-kanzler/starten', { method: 'POST', signal: AbortSignal.timeout(150_000) });
      const e = await s.json().catch(() => null) as { chat?: { sessionId: string; accountId?: string }; error?: string } | null;
      if (!s.ok || !e?.chat) throw new Error(e?.error || `HTTP ${s.status}`);
      return e.chat;
    })()
      .then(chat => { if (abgebrochen) return; setCoachChat(chat); setCoachTitel('Kanzler'); setCoachFehler(null); setCoachGeladen(true); })
      .catch(e => { if (abgebrochen) return; setCoachFehler(`Dein Kanzler konnte nicht geöffnet werden: ${e instanceof Error ? e.message : String(e)}`); setCoachGeladen(true); });
    return () => { abgebrochen = true; };
  }, [showGuide, authEnabled, user?.role, coachVersuch]);
  useEffect(() => {
    if (!showGuide || authEnabled === null || (authEnabled && user?.role !== 'admin')) return;
    let abgebrochen = false;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 20_000);
    let wiederholen: ReturnType<typeof setTimeout> | undefined;
    setCoachGeladen(false);
    fetch('/api/guide/board', { signal: controller.signal })
      .then(async r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (!d?.konfig || typeof d.konfig !== 'object') throw new Error('Antwort ohne Coach-Konfiguration');
        const coach = d.konfig.metaKoordinator;
        if (coach != null && (typeof coach.sessionId !== 'string' || !coach.sessionId)) {
          throw new Error('Coach-Zuordnung ohne Sitzungskennung');
        }
        return coach;
      })
      .then(coach => {
        if (abgebrochen) return;
        // /board liefert bereits die Projektion dieses Hauses. Nur ein erfolgreicher
        // Abruf ohne Zuordnung darf den Startknopf freigeben.
        setCoachChat(coach ?? null);
        setCoachTitel(coach?.label || 'Coach');
        setCoachFehler(null);
        setCoachGeladen(true);
      })
      .catch(e => {
        if (abgebrochen) return;
        setCoachFehler(controller.signal.aborted ? 'Zeitüberschreitung beim Laden des Coach' : `Coach konnte nicht geladen werden: ${String(e)}`);
        setCoachGeladen(true);
        wiederholen = setTimeout(() => setCoachVersuch(v => v + 1), 30_000);
      })
      .finally(() => clearTimeout(timeout));
    // Nur fuer das Etikett ("Dev"/"Partner") an der lokalen Coach-Spalte —
    // der Coach selbst ist bereits haus-eigen eingetragen (metaKoordinator/
    // metaKoordinatorPartner, siehe guide.ts konfigOeffentlich).
    fetch('/api/guide/haus')
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!abgebrochen && (d?.haus === 'dev' || d?.haus === 'partner')) setEigenesHaus(d.haus); })
      .catch(() => { /* Etikett bleibt leer, der Chat funktioniert trotzdem */ });
    return () => { abgebrochen = true; controller.abort(); clearTimeout(timeout); clearTimeout(wiederholen); };
  }, [showGuide, authEnabled, user?.role, coachVersuch]);
  useEffect(() => {
    const VORHANG_MAX_MS = 12000;
    let notbremse: ReturnType<typeof setTimeout> | undefined;
    const an = (e: Event) => {
      const d = (e as CustomEvent).detail as { aktiv?: boolean; was?: string };
      clearTimeout(notbremse);
      if (d?.aktiv) {
        setUmbau({ aktiv: true, was: d.was || 'Fenster wird hergerichtet' });
        // Die Notbremse HEBT den Vorhang — sie beschriftet ihn nicht nur. Die erste
        // Fassung setzte bloss einen Text ("der Vorhang geht auf") und liess ihn
        // unten; die Ansage stimmte dann nicht mit dem ueberein, was zu sehen war.
        notbremse = setTimeout(() => {
          console.warn('[guide] Umbau-Vorhang nach %d ms selbst geoeffnet — das Herrichten hat sich nicht zurueckgemeldet.', VORHANG_MAX_MS);
          setUmbau({ aktiv: false, was: '' });
        }, VORHANG_MAX_MS);
      } else {
        setUmbau({ aktiv: false, was: '' });
      }
    };
    window.addEventListener('guide-umbau', an);
    return () => { window.removeEventListener('guide-umbau', an); clearTimeout(notbremse); };
  }, []);
  // Idle-eviction bookkeeping (bounds RAM). `mountedLastUsed` tracks the last
  // time each mounted project was active or driven headlessly. `browserOnlyMounts`
  // marks projects mounted purely on-demand for browser commands (never opened by
  // the user) — only those are eviction candidates, so background chats the user
  // opened are never torn down.
  const mountedLastUsed = useRef<Map<string, number>>(new Map());
  const browserOnlyMounts = useRef<Set<string>>(new Set());
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | null>(null);
  const [editTarget, setEditTarget] = useState<Project | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  // cuiStates + serverAlive from shared SessionStore (single WS)
  const { sendWs, addMessageHandler, sessionStates, sessionStatesTick } = useSessionStore();
  // Gehaltene Buehne vom Server uebernehmen (lib/useBuehneHalten.ts). Nur der Betreiber, nie
  // das Schattenfenster: dort waeren es 18 Webviews auf dem Server, ohne Rafaels Anmeldungen.
  const haltProjektIds = useMemo(() => projects.map(p => p.id), [projects]);
  useBuehneHalten({
    aktiv: istBetreiberGuide && new URLSearchParams(window.location.search).get('shadow') !== '1',
    nutzer: anheftNutzer,
    projektIds: haltProjektIds,
    addMessageHandler,
  });
  // 'waiting' = a chat's turn ended but background work it started is still running
  // (yellow). Orange ('needs_attention') is reserved for "paused, waiting for Rafael".
  const [projectAttention, setProjectAttention] = useState<Record<string, 'working' | 'waiting' | 'needs_attention' | 'idle'>>({}); // projectId → state
  // Total open (non-finished, non-sub) chats across ALL workspaces — same rule as
  // AllChatsView/"AC" panel (not manualFinished, unfiltered by liveness/stuck: a
  // dead-but-never-finished chat is still "open" per Rule 1). Shown next to the
  // AC button so the count is visible without opening the panel.
  const [openChatsTotal, setOpenChatsTotal] = useState(0);
  // Same rule, bucketed per project — shown as "(N)" in each workspace tab so the
  // per-tab numbers can be summed and cross-checked against openChatsTotal above.
  // Deliberately does NOT sum to openChatsTotal: sessions with no resolvable
  // project (UNASSIGNED_PROJECT_NAME server-side) can't be attributed to any tab —
  // that gap is exactly AC's "Kein Workspace" group.
  const [openCountByProject, setOpenCountByProject] = useState<Record<string, number>>({});
  const [showMission, setShowMission] = useState(false);
  /* DER GRUPPENRAUM IST TEIL DES GUIDES (Rafael 03.11.2026 16:10Z: „der Gruppenraum wird einfach als Teil des
   * Guides aufgehoben“; die Tool-Ansicht ausserhalb sei „extremst verwirrend“). Er oeffnet als Fenster ueber
   * der aktuellen Flaeche; der Guide bleibt an. Dieselbe Komponente setzt Gelesen-Stand und rote Zahl. */
  const [gruppenraumOffen, setGruppenraumOffen] = useState(false);
  useEffect(() => {
    if (!gruppenraumOffen) return;
    const zu = (e: KeyboardEvent) => { if (e.key === 'Escape') setGruppenraumOffen(false); };
    window.addEventListener('keydown', zu);
    return () => window.removeEventListener('keydown', zu);
  }, [gruppenraumOffen]);
  const [showAllChats, setShowAllChats] = useState(false);
  /*
   * WAS UEBER DER BUEHNE LIEGT, RAEUMEN — OHNE RAFAELS TEXT ZU VERWERFEN (ASSIOEFFNENR P2-b).
   * Mission Control baut beim Schliessen ab; seine Eingaben sind ueber lib/gesicherterEntwurf
   * abgelegt und kommen beim naechsten Oeffnen zurueck. Steht dort Text, der sich NICHT ablegen
   * liess (Speicher voll/gesperrt), bleibt Mission Control offen, und der Aufrufer bekommt den
   * Grund (null = geraeumt). „Alle Chats" sichert seine Entwuerfe selbst (CuiLitePanel).
   */
  const showMissionRef = useRef(showMission);
  showMissionRef.current = showMission;
  const abdeckungRaeumen = useCallback((): string | null => missionAbdeckungRaeumen(
    showMissionRef.current, () => { setShowMission(false); setShowAllChats(false); },
  ), []);
  const [pendingActivation, setPendingActivation] = useState<Array<{ projectId: string; conversations: Array<{ sessionId: string; accountId: string }> }> | null>(null);
  const [missingSessions, setMissingSessions] = useState<Record<string, number>>({}); // projectId → count of unmounted sessions

  // Auto-reload when bundle changes (detects Syncthing-triggered rebuilds)
  const currentBundleRef = useRef(
    document.querySelector('script[src*="/assets/index-"]')?.getAttribute('src') || ''
  );
  const checkForUpdate = useCallback(async () => {
    try {
      const resp = await fetch('/?_v=' + Date.now(), { headers: { 'Accept': 'text/html' }, signal: AbortSignal.timeout(20000) });
      if (!resp.ok) return;
      const html = await resp.text();
      const match = html.match(/src="(\/assets\/index-[^"]+)"/);
      if (match && match[1] && match[1] !== currentBundleRef.current) {
        console.log(`[AutoReload] Bundle changed: ${currentBundleRef.current} → ${match[1]}`);
        neuLadenWennMoeglich('neues Bundle');
      }
    } catch (err) {
      console.warn('[App] checkForUpdate failed:', err);
    }
  }, [neuLadenWennMoeglich]);

  // Control API message handler (via shared SessionStore WebSocket)
  useEffect(() => {
    return addMessageHandler((msg: any) => {
      // Server assigns a stable clientId per WS connection. Stash on window
      // so any component (BrowserPanel) can echo it back via /browser-panel/
      // ready and the server can route follow-up eval/click/etc. to THIS
      // specific frontend instead of broadcasting (which lets non-owner
      // clients race to "Panel not found" and win).
      if (msg.type === 'client:hello' && typeof msg.clientId === 'string') {
        (window as unknown as { __CUI_CLIENT_ID__?: string }).__CUI_CLIENT_ID__ = msg.clientId;
        // Headless shadow client (server-side Electron under Xvfb, loaded
        // with ?shadow=1): identify to the server so screenshots.ts can use
        // this client as the always-paintable capture fallback.
        if (new URLSearchParams(window.location.search).get('shadow') === '1') {
          sendWs({ type: 'client:register', shadow: true });
        }
      }
      // Control API: switch project
      if (msg.type === 'control:project-switch' && msg.projectId) {
        setActiveId(msg.projectId);
      }
      // Browser mount-on-demand: mount project in the background when
      // a browser-panel API call targets an unmounted project. Does NOT
      // switch activeId — the user stays on their current project.
      // The target project renders with visibility:hidden, so its
      // BrowserPanel listeners are active and can receive commands.
      //
      // If the panel is already in the DOM (any prior mount this session),
      // setMounted is a no-op → BrowserPanel's mount-effect does NOT fire
      // again → the server's ensurePanelMounted() never sees a /ready POST
      // and times out. Confirm readiness explicitly for that path — but
      // ONLY on evidence: the panel's element must actually be in the DOM.
      // The previous shortcut trusted mountedLastUsed, yet that timestamp
      // is written the moment a mount STARTS — a second command arriving
      // during the 3-8s cold mount confirmed a panel that did not exist
      // yet, the server recorded phantom ownership and dispatched
      // eval/click into the void ("panel may not be mounted" timeouts
      // while the hidden workspace was still building its React tree).
      if (msg.type === 'control:mount-for-browser' && msg.projectId) {
        mountedLastUsed.current.set(msg.projectId, Date.now());
        browserOnlyMounts.current.add(msg.projectId);
        setMounted(prev => {
          if (prev.has(msg.projectId)) return prev;
          return new Set(prev).add(msg.projectId);
        });
        if (msg.panelId) {
          const domId = String(msg.panelId).replace(/^#/, '');
          const panelInDom = document.querySelector(`[data-node-id="${domId}"]`) !== null;
          if (panelInDom) {
            const clientId = (window as unknown as { __CUI_CLIENT_ID__?: string }).__CUI_CLIENT_ID__;
            fetch('/api/browser-panel/ready', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ panelId: msg.panelId, clientId }),
            }).catch(() => {});
          }
          // Not in DOM: the mount just started — BrowserPanel's own
          // mount-effect POSTs /ready when it truly exists. No early claim.
        }
      }
      // Activation: switch to target project, hide MC, pass plan as prop
      if (msg.type === 'control:activate-conversations' && msg.plan?.length > 0) {
        const firstProjectId = msg.plan[0].projectId;
        if (firstProjectId) {
          setPendingActivation(msg.plan);
          setActiveId(firstProjectId);
          setShowMission(false);
        }
      }
      // Forward sync-related messages to ProjectTabs via window.postMessage
      if (msg.type === 'cui-update-available' || (msg.type === 'cui-sync' && msg.auto)) {
        window.postMessage(JSON.stringify(msg), '*');
        if (msg.type === 'cui-update-available') {
          setTimeout(checkForUpdate, 8000);
        }
      }
      // Screenshot request
      if (msg.type === 'control:screenshot-request' && msg.panel) {
        (async () => {
          // Set when we force a background workspace painted for the capture;
          // always invoked in `finally` so the workspace returns to hidden even
          // if the capture throws (defensive — no lingering visible workspace).
          let restoreWorkspaceVisibility: (() => void) | null = null;
          try {
            const panelId: string = msg.panel;
            let target: HTMLElement | null = null;
            let matchedVia = '';
            const findTarget = (): { el: HTMLElement | null; via: string } => {
              if (panelId === 'full') return { el: document.getElementById('root'), via: 'root' };
              let el = document.querySelector<HTMLElement>(`[data-node-id="${panelId}"]`);
              if (el) return { el, via: 'data-node-id-exact' };
              const allNodes = document.querySelectorAll<HTMLElement>('[data-node-id]');
              for (const node of allNodes) {
                const nid = node.getAttribute('data-node-id') || '';
                if (nid.startsWith(panelId)) return { el: node, via: `data-node-id-partial:${nid}` };
              }
              el = document.querySelector<HTMLElement>(`[data-panel="${panelId}"]`);
              if (el) return { el, via: 'data-panel' };
              const candidates = document.querySelectorAll<HTMLElement>('.flexlayout__tab');
              for (const tab of candidates) {
                if (tab.querySelector(`[data-panel="${panelId}"]`)) return { el: tab, via: 'flexlayout-tab-child' };
              }
              return { el: null, via: '' };
            };
            let found = findTarget();
            target = found.el;
            matchedVia = found.via;
            // Panel not in the DOM at all — try to activate it, then retry.
            if (!target) {
              sendWs({ type: 'control:ensure-panel', component: panelId });
              sendWs({ type: 'control:select-tab', target: panelId });
              await new Promise(r => setTimeout(r, 1500));
              found = findTarget();
              target = found.el;
              matchedVia = found.via ? `auto-activated:${found.via}` : '';
            }
            if (!target) {
              const existingIds = Array.from(document.querySelectorAll<HTMLElement>('[data-node-id]'))
                .map(el => el.getAttribute('data-node-id'));
              throw new Error(`Panel "${panelId}" not found. Available: [${existingIds.join(', ')}]`);
            }
            // An Electron <webview> renders in a separate web-contents: html2canvas
            // cannot see into it, but webview.capturePage() can. A background tab
            // is not painted though, so such panels are briefly brought to the
            // front (select-tab) before capture — select-tab never creates a panel.
            const webviews = target.querySelectorAll('webview');
            const webview = (panelId !== 'full' && webviews.length === 1) ? (webviews[0] as any) : null;
            const useWebviewCapture = !!webview && !!window.electronAPI?.isElectron
              && typeof webview.capturePage === 'function';
            if (useWebviewCapture) {
              // Electron does NOT composite a <webview> whose owning workspace is
              // visibility:hidden (a background workspace) → capturePage() returns
              // 0x0. The workspace stays mounted (renderer alive, so eval/click
              // keep working) but unpainted. Force the owning workspace painted for
              // the duration of the capture; it remains at zIndex:-1, occluded by
              // the opaque active workspace, so there is NO visible flicker. The
              // restore runs in `finally` regardless of outcome.
              const wsRoot = target.closest<HTMLElement>('[data-workspace-root]');
              if (wsRoot && getComputedStyle(wsRoot).visibility === 'hidden') {
                const prevVisibility = wsRoot.style.visibility;
                const prevOpacity = wsRoot.style.opacity;
                restoreWorkspaceVisibility = () => {
                  wsRoot.style.visibility = prevVisibility;
                  wsRoot.style.opacity = prevOpacity;
                };
                wsRoot.style.visibility = 'visible';
                wsRoot.style.opacity = '1';
                // Let Electron composite the now-visible webview before capturing
                // (two RAFs + a settle delay so the renderer paints a frame).
                await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(() => r(null))));
                await new Promise(r => setTimeout(r, 350));
              }
              // capturePage() yields pixels only for a painted webview. A
              // background TAB within the (now visible) workspace is display:none
              // in flexlayout, so its <webview> is not composited and capture
              // returns 0x0. Bring the tab to the front and POLL until it
              // actually has layout (width>0) — the old fixed 1300ms often fired
              // before flexlayout switched the active tab / before the webview
              // re-composited. Then give the webview a couple of frames + a
              // settle to paint before capturing.
              if (target.getBoundingClientRect().width === 0) {
                sendWs({ type: 'control:select-tab', target: panelId });
                const tabDeadline = Date.now() + 4000;
                while (Date.now() < tabDeadline && target.getBoundingClientRect().width === 0) {
                  await new Promise(r => setTimeout(r, 150));
                }
                await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(() => r(null))));
                await new Promise(r => setTimeout(r, 400));
              }
            } else {
              // html2canvas needs the panel actually visible — activate if hidden.
              let rect = target.getBoundingClientRect();
              if (rect.width === 0 || rect.height === 0) {
                sendWs({ type: 'control:ensure-panel', component: panelId });
                sendWs({ type: 'control:select-tab', target: panelId });
                await new Promise(r => setTimeout(r, 1500));
                found = findTarget();
                target = found.el ?? target;
                rect = target.getBoundingClientRect();
              }
              if (rect.width === 0 || rect.height === 0) {
                throw new Error(`Panel "${panelId}" hidden (${rect.width}x${rect.height})`);
              }
            }
            const contentWait = msg.contentWait ?? 2000;
            if (contentWait > 0) await new Promise(r => setTimeout(r, contentWait));
            const nachweisId=typeof msg.nachweisId==='string'?msg.nachweisId:undefined;
            let nativ: {url:string;partition:string;loginMaske:boolean}|undefined;
            if(nachweisId){
              if(!useWebviewCapture)throw new Error('App-Nachweis benötigt eine echte Electron-Webview-Aufnahme.');
              nativ=await inspectNativeApp(webview);
            }
            let dataUrl: string;
            let width: number;
            let height: number;
            if (useWebviewCapture) {
              // capturePage() can hang indefinitely when the target webview was
              // never actually painted (e.g. a background tab that select-tab
              // failed to bring to front, or a detached web-contents). Without a
              // bound the whole capture IIFE stalls silently — no success POST,
              // no error POST — and the server times out with a useless 408.
              // Race it against a deadline so a hang throws into the catch below.
              // The message carries "hidden" on purpose: the catch classifies
              // that as `deferred: true` (same bucket as the 0x0-empty path), so
              // in a multi-frontend setup another client where the panel IS
              // painted can still win the race — and a single-frontend caller
              // gets an actionable 422 ("webview not painted") instead of a
              // mystery 408 hang.
              const raceCapture = () => Promise.race([
                webview.capturePage(),
                new Promise<never>((_, rej) =>
                  setTimeout(() => rej(new Error(
                    `capturePage timeout for "${panelId}" — webview not painted (likely a hidden/background tab)`,
                  )), 8000),
                ),
              ]);
              // A <webview> that just became visible (background tab we only now
              // brought to front) can hand back one empty (0x0) frame before it
              // composites. Retry once after a short settle instead of instantly
              // reporting the 0x0 deferred — this is what makes background-tab
              // capture actually succeed rather than just fail loudly.
              let image = await raceCapture();
              if (image.getSize().width === 0 || image.getSize().height === 0) {
                await new Promise(r => setTimeout(r, 700));
                image = await raceCapture();
              }
              dataUrl = image.toDataURL();
              const size = image.getSize();
              width = size.width;
              height = size.height;
              matchedVia += '+webview-capturePage';
            } else {
              const html2canvas = (await import('html2canvas')).default;
              const canvas = await html2canvas(target, {
                backgroundColor: aufnahmeHintergrund(), onclone: farbenFuerHtml2canvas, scale: 1, useCORS: true, logging: false, allowTaint: true,
              });
              dataUrl = canvas.toDataURL('image/png');
              width = canvas.width;
              height = canvas.height;
            }
            if (!width || !height) {
              // Empty capture — a background <webview> isn't painted, so
              // capturePage yields nothing here. Report this explicitly with
              // `deferred: true` so the server can distinguish "this frontend
              // can't capture, try another one" from "real failure". In a
              // multi-frontend setup a frontend where the panel IS visible
              // can still POST a successful capture and beat the deferred
              // report (success wins on the server). In a single-frontend
              // setup the server times the wait out and surfaces the deferred
              // diagnostic to the caller instead of a generic timeout.
              console.warn(`[Screenshot] ${panelId}: empty capture (${width}x${height}), deferring`);
              fetch(`/api/screenshot/${msg.panel}/error`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  error: `Panel "${panelId}" rendered empty (${width}x${height}) — likely a background webview that Electron did not paint. Re-try after the workspace becomes visible, or fall back to /api/capture (Playwright).`,
                  deferred: true,
                }),
                signal: AbortSignal.timeout(20000),
              }).catch(() => {});
              return;
            }
            if(nativ&&webview.getURL()!==nativ.url)throw new Error('App wechselte während der Aufnahme den Bildschirm.');
            const captureResponse=await fetch(`/api/screenshot/${panelId}`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ dataUrl, width, height, ...(nachweisId?{nachweisId,nativ}:{}) }),
              signal: AbortSignal.timeout(20000),
            });
            if(!captureResponse.ok)throw new Error('Aufnahme nicht angenommen: '+await captureResponse.text());
            console.log(`[Screenshot] Captured ${panelId} via ${matchedVia} (${width}x${height})`);
          } catch (err) {
            const errMsg = err instanceof Error ? err.message : String(err);
            // Classify panel-not-found / panel-hidden as `deferred: true` so
            // another connected frontend where the panel IS mounted can still
            // win the capture race. Hard errors (capturePage threw, fetch
            // failed, html2canvas crashed) stay non-deferred and abort the
            // server-side wait immediately — those are real failures, no
            // other frontend will fix them. Without this classification, a
            // single non-owner client's "Panel not found" used to 422 the
            // whole request before the owner's successful capture landed.
            const isPanelMiss = errMsg.includes('not found') || errMsg.includes('hidden');
            console.error('[Screenshot] Failed:', msg.panel, errMsg, isPanelMiss ? '(deferred)' : '(hard)');
            fetch(`/api/screenshot/${msg.panel}/error`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ error: errMsg, deferred: isPanelMiss }),
              signal: AbortSignal.timeout(20000),
            }).catch(() => {}); // silent-ok: error report after screenshot failure is best-effort
          } finally {
            // Always restore a force-painted workspace back to hidden — even if
            // the capture threw — so we never leave a background workspace visible.
            restoreWorkspaceVisibility?.();
          }
        })();
      }
      // Being DRIVEN counts as being used — refresh the idle-eviction clock
      // for the workspace that owns the addressed panel.
      //
      // Without this, a headless-only mount could be evicted mid-session
      // (2026-09-02): `mountedLastUsed` was only written by
      // control:mount-for-browser, and that message is not sent while the
      // server already knows an owner (ensurePanelMountedOnClient
      // short-circuits on known ownership). So a workspace that an agent
      // drove continuously — navigate, click, type, eval — looked perfectly
      // idle to the sweep, and after MOUNT_IDLE_TTL_MS its LayoutManager and
      // <webview> were unmounted: login session gone, wizard progress gone,
      // and the next command silently re-mounted a blank browser. This is
      // the client-side half of the same failure the server-side ownership
      // fix addresses; either half alone still loses the page.
      if (typeof msg.type === 'string' && msg.type.startsWith('control:browser-') && msg.panelId) {
        const drivenDomId = String(msg.panelId).replace(/^#/, '');
        const drivenProjectId = document
          .querySelector(`[data-node-id="${drivenDomId}"]`)
          ?.closest<HTMLElement>('[data-workspace-root]')
          ?.dataset.workspaceRoot;
        if (drivenProjectId) mountedLastUsed.current.set(drivenProjectId, Date.now());
      }
      // Commands that await an ack (eval/click/url/type/keypress) for a panel
      // that is NOT mounted in this window: nobody would answer, the caller
      // would sit out its whole timeout and be told "owner is still alive".
      // Say so at once instead; the server then revokes this window's stale
      // claim (it is only ever dropped on disconnect). 06.10.2026: every eval
      // on a stage Rafael had navigated away from timed out like this.
      if ((msg.type === 'control:browser-eval' || msg.type === 'control:browser-type'
        || msg.type === 'control:browser-keypress')
        && typeof msg.panelId === 'string' && typeof msg.correlationId === 'string'
        && !istBrowserPanelGemountet(msg.panelId)) {
        fetch('/api/browser-panel/eval-result', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            correlationId: msg.correlationId,
            nichtGemountet: true,
            error: `Panel ${msg.panelId} ist in diesem Fenster nicht geöffnet (Bühne oder Workspace gewechselt, Reiter ausgehängt). `
              + 'Der Besitz wurde freigegeben — die Kachel muss wieder sichtbar sein, bevor sie Befehle annimmt.',
          }),
        }).catch(err => console.error('[App] eval-result (nicht gemountet) failed', err));
        return;
      }
      // Browser panel navigation — relayed to the matching BrowserPanel via a
      // window event (BrowserPanel has no WS of its own).
      if (msg.type === 'control:browser-navigate' && msg.panelId && msg.url) {
        window.dispatchEvent(new CustomEvent('browser-navigate', {
          detail: { panelId: msg.panelId, url: msg.url },
        }));
      }
      // Device-viewport switch (desktop/iphone/ipad) for a browser panel.
      if (msg.type === 'control:browser-device' && msg.panelId && msg.device) {
        window.dispatchEvent(new CustomEvent('browser-device', {
          detail: { panelId: msg.panelId, device: msg.device },
        }));
      }
      // Headless JS injection into a browser panel's webview. Result is logged
      // to the panel's console as `[EXEC:<requestId>] ...` so it can be polled
      // via GET /api/browser-console/<panelId> after the call.
      if (msg.type === 'control:browser-execute' && msg.panelId && msg.script) {
        window.dispatchEvent(new CustomEvent('browser-execute', {
          detail: { panelId: msg.panelId, requestId: msg.requestId || 'noid', script: msg.script },
        }));
      }
      // Synchronous JS eval — value is POSTed back to /api/browser-panel/eval-result
      // by the BrowserPanel handler. Server matches the response to the pending
      // /eval HTTP call via correlationId.
      if (msg.type === 'control:browser-eval' && msg.panelId && msg.script && msg.correlationId) {
        window.dispatchEvent(new CustomEvent('browser-eval', {
          detail: { panelId: msg.panelId, correlationId: msg.correlationId, script: msg.script },
        }));
      }
      // Reload a browser panel — clearCache uses reloadIgnoringCache() so a
      // stale JS bundle (after a Next.js rebuild) actually gets re-downloaded.
      if (msg.type === 'control:browser-reload' && msg.panelId) {
        window.dispatchEvent(new CustomEvent('browser-reload', {
          detail: { panelId: msg.panelId, clearCache: !!msg.clearCache },
        }));
      }
      // Type a string into the focused element of a browser panel.
      if (msg.type === 'control:browser-type' && msg.panelId && msg.correlationId) {
        window.dispatchEvent(new CustomEvent('browser-type', {
          detail: {
            panelId: msg.panelId,
            correlationId: msg.correlationId,
            text: msg.text,
            delayMs: msg.delayMs,
          },
        }));
      }
      // Send a named keypress (Enter/Tab/Escape/Ctrl+S) to the focused element.
      if (msg.type === 'control:browser-keypress' && msg.panelId && msg.correlationId) {
        window.dispatchEvent(new CustomEvent('browser-keypress', {
          detail: {
            panelId: msg.panelId,
            correlationId: msg.correlationId,
            key: msg.key,
            modifiers: msg.modifiers ?? [],
          },
        }));
      }
      // Clear every <webview> partition's cache + service-worker registrations
      // (Electron main process only — see electron/main.cjs clearAllBrowserStorage)
      // and hard-reload all browser panels. Fixes a "poisoned origin" webview
      // that hangs on EVERY navigation (e.g. a corrupted/hung service worker) —
      // the old menu item only cleared session.defaultSession (the CUI shell's
      // own session), which never touched the embedded pages. Ack goes through
      // the same /eval-result endpoint as browser-eval so the server's
      // POST /api/browser-panel/:panelId/clear-storage can resolve.
      if (msg.type === 'control:browser-clear-storage' && msg.correlationId) {
        (async () => {
          const correlationId = msg.correlationId;
          let body: { correlationId: string; result?: unknown; error?: string };
          if (!window.electronAPI?.isElectron) {
            body = { correlationId, error: 'not running in Electron — clear-storage only applies to <webview> partitions' };
          } else if (!window.electronAPI.clearBrowserStorage) {
            body = { correlationId, error: 'this Electron binary predates clear-browser-storage (electron/main.cjs + preload.cjs) — redistribute the app' };
          } else {
            try {
              const result = await window.electronAPI.clearBrowserStorage();
              window.dispatchEvent(new CustomEvent('nuclear-refresh'));
              body = { correlationId, result };
            } catch (err) {
              body = { correlationId, error: (err as Error)?.message || String(err) };
            }
          }
          fetch('/api/browser-panel/eval-result', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          }).catch(() => {}); // silent-ok: server times out on its own if this fails
        })();
      }
      // Full-window refresh (Cmd+R equivalent). Triggered by
      // POST /api/control/window/refresh so headless callers can pick up a
      // fresh Vite bundle without asking the user to hit Cmd+R.
      // Ein Schattenfenster antwortet auf diese beiden Nachrichten nur, wenn der
      // Server es SELBST ausdruecklich als Schatten-Ziel markiert hat
      // (erlaubtSchatten:true — server/routes/control.ts sendet das nur an
      // getShadowClientIds() bzw. wenn der Aufrufer /oeffne-buehne bewusst mit
      // erlaubtSchatten:true aufgerufen hat). Ohne diese Markierung bleibt der
      // Shadow stumm, genau wie zuvor. Rafael, 16.09.2026: eine Buehne muss
      // koennen, was er selbst im CUI koennte — ein Fenster oeffnen, sich
      // anmelden, sich alles ansehen; der Shadow ist dafuer ein echtes, aber
      // unbeobachtetes Electron-Fenster, kein Ersatz fuer seine Oberflaeche.
      if (msg.type === 'control:guide-fenster' && typeof msg.abfrageId === 'string') {
        const istSchatten = new URLSearchParams(window.location.search).get('shadow') === '1';
        if (!guideSteuerungErlaubt.current || (istSchatten && msg.erlaubtSchatten !== true)) return;
        const clientId = (window as unknown as { __CUI_CLIENT_ID__?: string }).__CUI_CLIENT_ID__;
        if (!clientId) return;
        fensterAbfrageBeantwortet(msg.abfrageId);
        const gezeigteBuehne = document.querySelector<HTMLElement>('[data-ai-id="guide-buehne-offen"]');
        const buehneId = gezeigteBuehne?.dataset.buehneId ?? buehnenLayoutRef.current?.bezug?.id ?? null;
        // Sichtbar heisst: links diese Bühne UND rechts ihre Schicht statt der leeren Fläche
        // (guideOeffnungsBeleg.ts) — nicht nur, dass das linke Element Fläche hat.
        const buehneSichtbar = !!buehneId && buehneNichtGezeichnet(document, buehneId) === null;
        // Bildschirm 2 im Vollbild meldet sich als solcher: ohne clientId ist er kein Ziel (ASSIOEFFNENR P2-3).
        const zusatz = fensterZusatz(document, buehneId, buehneSichtbar, b2VollbildRef.current);
        void fetch('/api/control/guide/fenster-ergebnis', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ abfrageId: msg.abfrageId, clientId, titel: document.title.slice(0, 300), fokus: document.hasFocus(), buehneId, buehnenTitel: gezeigteBuehne?.dataset.buehneTitel ?? null, buehneStandAm: gezeigteBuehne?.dataset.buehneStandAm ?? null, buehneSichtbar, ...zusatz, zuletztAktiv: zuletztAktiv.current, gespraech: ui.gespraech, nativeCapture: typeof window.electronAPI?.captureStage === 'function', nativeProfileProbe: typeof window.electronAPI?.probeBrowserProfiles === 'function',
            // Bundle-Stand und vorgemerktes Neuladen: eine Sichtmeldung gilt nur bis dahin (Messung 14.10. 08:45Z).
            build: __BUILD_TIME__, neuladenAnstehend: reloadOffen.current !== null,
            // Rafaels eigene Navigationsschritte (lib/rafaelNavigation.ts): der Server hängt den Stand an den Öffnungsauftrag.
            handNav: rafaelNavStand().n }),
          signal: AbortSignal.timeout(5000),
        }).then(r => { if (!r.ok) throw new Error(`Fensterabfrage HTTP ${r.status}`); })
          .catch(e => console.error('[guide-fenster]', e));
      }
      if (msg.type === 'control:guide-oeffne-buehne') {
        if (!guideSteuerungErlaubt.current) return;
        const istSchatten = new URLSearchParams(window.location.search).get('shadow') === '1';
        if (istSchatten && msg.erlaubtSchatten !== true) return;
        if (typeof msg.buehneId !== 'string' || typeof msg.auftragId !== 'string'
          || typeof msg.clientId !== 'string' || typeof msg.expiresAt !== 'number') return;
        if (Date.now() >= msg.expiresAt) {
          // Nicht still verwerfen: sonst wartet der Aufrufer 15 s auf eine Zeitüberschreitung,
          // die in Wahrheit eine Ablehnung ist (z. B. Uhr dieses Rechners vor dem Server).
          void fetch('/api/control/guide/oeffnung-ergebnis', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ clientId: msg.clientId, buehneId: msg.buehneId, auftragId: msg.auftragId, ok: false, grund: 'abgelaufen',
              error: `Auftrag kam abgelaufen an (Uhr dieses Fensters ${new Date().toISOString()}, gültig bis ${new Date(msg.expiresAt).toISOString()}).` }),
            signal: AbortSignal.timeout(5000),
          }).catch(e => console.error('[guide-oeffnen-abgelaufen]', e));
          return;
        }
        // Role and stage access are checked by the API before anything is opened.
        // handNav: Rafaels Navigationsstand bei Auftragserteilung (vom Server aus der Fensterabfrage),
        // sonst der beim Empfang — hat er danach selbst navigiert, verwirft GuidePanel den Auftrag.
        setGuideOeffnungsAuftrag({ clientId: msg.clientId, buehneId: msg.buehneId, auftragId: msg.auftragId, expiresAt: msg.expiresAt,
          handNav: typeof msg.handNav === 'number' ? msg.handNav : rafaelNavStand().n, ...(msg.nurAngebot === true ? { nurAngebot: true } : {}) });
        setShowGuide(true);
        // Mission Control und „Alle Chats“ räumt erst GuidePanel, wenn es den Auftrag annimmt
        // (abdeckungRaeumen) — eine Ablehnung lässt sie stehen (ASSIOEFFNENR P2-b).
      }
      if (msg.type === 'control:window-refresh') {
        console.log('[App] window-refresh requested via API — reloading');
        // Small delay so the WS ack flushes before we tear the renderer down.
        // Auch dieser Weg geht durch die Schleuse: der Rundruf nach einem Neubau
        // traf Rafael sonst mitten im Lesen (16:56Z). Der Bundle-Check oben holt
        // dasselbe Neuladen nach, sobald er die Karte verlassen hat.
        setTimeout(() => neuLadenWennMoeglich('window-refresh'), 100);
      }
      // Eager-mount diagnostics — dump mounted projects, the LayoutManagers
      // currently rendered, and a count of <webview> elements.
      if (msg.type === 'control:debug-render-state' && msg.correlationId) {
        window.dispatchEvent(new CustomEvent('debug-render-state', {
          detail: { correlationId: msg.correlationId },
        }));
      }
      // DOM introspection
      if (msg.type === 'control:list-panels') {
        const panels = Array.from(document.querySelectorAll<HTMLElement>('[data-node-id]')).map(el => {
          const rect = el.getBoundingClientRect();
          return { nodeId: el.getAttribute('data-node-id'), visible: rect.width > 0 && rect.height > 0, size: `${Math.round(rect.width)}x${Math.round(rect.height)}` };
        });
        fetch('/api/screenshot/panels', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ panels, timestamp: new Date().toISOString() }),
          signal: AbortSignal.timeout(20000),
        }).catch(() => {}); // silent-ok: control message panel-list response is best-effort
      }
      // CPU Profile
      if (msg.type === 'control:cpu-profile' && window.electronAPI?.cpuProfile) {
        (async () => {
          try {
            const result = await window.electronAPI!.cpuProfile();
            sendWs({ type: 'cpu-profile-result', data: result });
          } catch (err) {
            sendWs({ type: 'cpu-profile-result', data: { error: String(err) } });
          }
        })();
      }
    });
  }, [addMessageHandler, sendWs, checkForUpdate, ui.gespraech]);

  // handleAttentionChange removed — projectAttention now computed directly from SessionStore + Conversations API

  // cuiState reset not needed — SessionStore tracks from WS events

  // === Project-level attention from SessionStore + Conversations API ===
  // Split into two effects:
  // 1. Conversation fetching (every 5s, depends only on projects)
  // 2. Attention computation (reacts to sessionStatesTick from WS events)
  const convCacheRef = useRef<Array<{ sessionId: string; projectId?: string; projectName: string; status: string; updatedAt: string; attentionState?: string; attentionReason?: string; isSubSession?: boolean; processAlive?: boolean; manualFinished?: boolean; customName?: string; subject?: string; liveness?: string }>>([]);
  const convTickRef = useRef(0);
  const [convTick, setConvTick] = useState(0);

  // Effect 1: Fetch conversations periodically
  useEffect(() => {
    if (!projects.length) return;
    const fetchConvs = () => {
      fetch(conversationsUrl('/api/mission/conversations'), { signal: AbortSignal.timeout(3000), priority: 'low' })
        .then(r => r.json())
        .then(data => {
          const convs = Array.isArray(data) ? data : data?.conversations || [];
          convCacheRef.current = convs.map((c: any) => ({
            sessionId: c.sessionId,
            projectId: c.projectId || '',
            projectName: c.projectName || '',
            status: c.status || 'completed',
            updatedAt: c.updatedAt || '',
            attentionState: c.attentionState,
            attentionReason: c.attentionReason,
            isSubSession: c.isSubSession || false,
            processAlive: c.processAlive || false,
            manualFinished: c.manualFinished || false,
            customName: c.customName || '',
            subject: c.subject || '',
            liveness: c.liveness,
          }));
          convTickRef.current++;
          setConvTick(convTickRef.current);
        })
        .catch(() => {}); // silent-ok: conversation polling failure keeps cached data; next interval retries
    };
    fetchConvs();
    const interval = setInterval(fetchConvs, 5000);
    return () => clearInterval(interval);
  }, [projects]);

  // Effect 2: Compute attention — fires on WS state changes AND conversation fetches
  useEffect(() => {
    if (!projects.length || convCacheRef.current.length === 0) return;

    const result: Record<string, 'working' | 'waiting' | 'needs_attention' | 'idle'> = {};
    const nameToId = new Map<string, string>();
    for (const p of projects) nameToId.set(p.name.toLowerCase(), p.id);
    const validIds = new Set(projects.map(p => p.id));

    const now = Date.now();
    for (const conv of convCacheRef.current) {
      if (!isOpenChat(conv)) continue;
      // Orphans are skipped for URGENCY only (Rule 3, utils/chatLifecycle.ts). Without
      // this a week-old dead one-shot delegation paints the workspace tab orange
      // forever, though the workspace's chat launcher correctly shows 0 active
      // (reported 2026-08-04: "Arch Worker" needs_attention with no active chat).
      // They remain OPEN and keep their tab — ProjectTabs derives visibility from the
      // Rule-1 count below, not from this pass. Conflating the two is what made AC
      // report 14 while the tabs summed to 13.
      if (isOrphanedChat(conv)) continue;
      // Sub-Session detection — see utils/subSession.ts for the rule (Rule 2).
      const isEffectivelySub = isSubSession(conv as any);
      // Sub-sessions belong to the sub-sessions workspace for attention tracking.
      // Prefer the stable projectId (keeps the tab color consistent with
      // QueueOverlay, which filters by projectId). Fall back to display-name
      // matching only for legacy/untagged sessions. Name matching is fragile:
      // two workspaces sharing a workDir — or a rename — can resolve one session's
      // name to a *different* project id, coloring the wrong tab orange while that
      // workspace's chat panel (path/id-keyed) stays empty.
      const projId = isEffectivelySub
        ? 'sub-sessions'
        : resolveProjectId(conv, validIds, nameToId);
      if (!projId) continue;

      // Prefer WS-based sessionStates (real-time), fall back to server attentionState (from conv list)
      const ss = sessionStates.get(conv.sessionId);
      const rawState = ss?.state || (conv.attentionState as any) || 'idle';
      const reason = ss?.reason || conv.attentionReason;

      // Stale detection: if "working" but no WS update in 90s, treat as needs_attention
      // UNLESS the process is still alive (it may just be running a long tool)
      let isStale = false;
      if (rawState === 'working') {
        // Stale if no WS update in 90s — applies even when process is alive (tool-heartbeats keep it fresh)
        const lastUpdate = ss?.since || (conv.updatedAt ? new Date(conv.updatedAt).getTime() : 0);
        if (lastUpdate > 0) {
          isStale = (now - lastUpdate) > 90_000;
        }
      }

      let effectiveState: 'working' | 'waiting' | 'needs_attention' | 'idle';
      if (rawState === 'working' && !isStale) {
        effectiveState = 'working';
      } else if (rawState === 'needs_attention' || isStale) {
        effectiveState = 'needs_attention';
      } else if (rawState === 'idle' && reason === 'background' && conv.processAlive) {
        // Turn ended, but a background task/agent/monitor is still running and will
        // re-invoke the session → yellow. Not orange: nobody has to click here yet.
        // (Rafael, 2026-09-02: orange must mean "paused, waiting for me".)
        effectiveState = 'waiting';
      } else if (rawState === 'idle' && reason === 'done') {
        // Session done → needs attention (user should review)
        effectiveState = 'needs_attention';
      } else if (conv.processAlive && rawState !== 'needs_attention') {
        // Process is alive → treat as working even if temporarily idle between tool calls
        effectiveState = 'working';
      } else if (!conv.processAlive && isOpenChat(conv)) {
        // Process stopped but still open (Rule 1) → needs attention (orange)
        effectiveState = 'needs_attention';
      } else {
        effectiveState = 'idle';
      }

      // Priority per workspace: needs_attention > working > waiting > idle
      const RANK = { needs_attention: 3, working: 2, waiting: 1, idle: 0 } as const;
      const existing = result[projId];
      if (!existing || RANK[effectiveState] > RANK[existing]) {
        result[projId] = effectiveState;
      }
    }

    // Total + per-project open counts — deliberately a SEPARATE pass, not derived
    // from `result` above: the attention loop skips liveness==='stuck' (Rule 3,
    // "needs looking at now"), but "open" here follows Rule 1 (present until
    // Finish is clicked), same rule /api/all-active-chats uses for AC's count.
    let openTotal = 0;
    const countResult: Record<string, number> = {};
    const unattributable: typeof convCacheRef.current = [];
    for (const conv of convCacheRef.current) {
      if (!isOpenChat(conv)) continue;
      if (isSubSession(conv as any)) continue;
      openTotal++;
      // Same attribution as the attention pass (utils/chatLifecycle.ts). Unresolvable
      // means the session belongs to no tab; it still counts toward openTotal and is
      // surfaced in AC's "Kein Workspace" group, so the buckets legitimately fall short
      // of the total by exactly that many — reported below rather than swallowed.
      const projId = resolveProjectId(conv, validIds, nameToId);
      if (projId) countResult[projId] = (countResult[projId] || 0) + 1;
      else unattributable.push(conv);
    }
    reportUnattributableOpenChats(unattributable);
    setOpenChatsTotal(prev => prev === openTotal ? prev : openTotal);
    setOpenCountByProject(prev => {
      const keys = new Set([...Object.keys(prev), ...Object.keys(countResult)]);
      for (const k of keys) if (prev[k] !== countResult[k]) return countResult;
      return prev;
    });

    setProjectAttention(prev => {
      const keys = new Set([...Object.keys(prev), ...Object.keys(result)]);
      let changed = false;
      for (const k of keys) {
        if (prev[k] !== result[k]) { changed = true; break; }
      }
      return changed ? result : prev;
    });
  }, [projects, sessionStatesTick, convTick]); // sessionStatesTick triggers on every WS event

  const handleMissingSessions = useCallback((projectId: string, count: number) => {
    setMissingSessions(prev => {
      if (prev[projectId] === count) return prev;
      return { ...prev, [projectId]: count };
    });
  }, []);

  const handleActivationProcessed = useCallback((processedProjectId?: string) => {
    if (!processedProjectId) {
      setPendingActivation(null);
      return;
    }
    setPendingActivation(prev => {
      if (!prev) return null;
      const remaining = prev.filter(p => p.projectId !== processedProjectId);
      return remaining.length > 0 ? remaining : null;
    });
  }, []);

  // Load projects: localStorage cache → instant, server → background update
  useEffect(() => {
    const CACHE_KEY = 'cui-projects-cache';

    // 1. Load from cache immediately (no "Loading workspace..." wait)
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const cachedProjects = JSON.parse(cached) as Project[];
        if (cachedProjects.length > 0) {
          setProjects(cachedProjects);
          const savedId = activeId || '';
          const validId = cachedProjects.find(p => p.id === savedId) ? savedId : (cachedProjects[0]?.id ?? '');
          if (validId !== activeId) setActiveId(validId);
          setLoaded(true);
        }
      }
    } catch (err) {
      console.warn('[App] localStorage project cache read failed:', err);
    }

    // 2. Fetch from server in background (updates cache)
    fetchProjects().then((serverProjects) => {
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(serverProjects)); } catch (err) { console.warn('[App] localStorage project cache write failed:', err); }
      setProjects(serverProjects);
      const savedId = activeId || '';
      const validId = serverProjects.find(p => p.id === savedId) ? savedId : (serverProjects[0]?.id ?? '');
      if (validId !== activeId) setActiveId(validId);
      setLoaded(true);
      for (const p of serverProjects) saveProject(p);
    }).catch((err) => {
      console.warn('[App] fetchProjects failed, using fallback:', err);
      // Server down — if cache was empty, use defaults
      setLoaded(prev => {
        if (prev) return prev; // Already loaded from cache
        setProjects(DEFAULT_PROJECTS);
        if (!activeId && DEFAULT_PROJECTS.length > 0) setActiveId(DEFAULT_PROJECTS[0].id);
        return true;
      });
    });
  }, []);

  // Report active project to server (for Control API state queries)
  useEffect(() => {
    if (activeId) {
      sendWs({ type: 'state-report', activeProjectId: activeId });
    }
  }, [activeId]);

  // Lazy-mount: only render LayoutManagers for projects that have been opened.
  // On-demand browser mounting is handled via control:mount-for-browser WS message.
  useEffect(() => {
    if (activeId) {
      mountedLastUsed.current.set(activeId, Date.now());
      // User opened this project → it is a real mount, not an eviction candidate.
      browserOnlyMounts.current.delete(activeId);
      setMounted(prev => {
        if (prev.has(activeId)) return prev;
        return new Set(prev).add(activeId);
      });
    }
  }, [activeId]);

  // Der Wirt einer angehefteten Buehne ist immer montiert — auch nach einem Neuladen,
  // bevor ihn jemand oeffnet — und faellt nie der Leerlauf-Raeumung zum Opfer.
  useEffect(() => {
    if (!angeheftetWirtId) return;
    browserOnlyMounts.current.delete(angeheftetWirtId);
    setMounted(prev => prev.has(angeheftetWirtId) ? prev : new Set(prev).add(angeheftetWirtId));
  }, [angeheftetWirtId]);

  // Idle-eviction sweep: unmount headless-only browser mounts that have not been
  // driven within MOUNT_IDLE_TTL_MS (never the active project, never user-opened
  // ones). Unmounting removes the project's LayoutManager + <webview> from the
  // DOM, freeing RAM. A later browser command re-mounts it on demand.
  useEffect(() => {
    const sweep = () => {
      const now = Date.now();
      setMounted(prev => {
        let next: Set<string> | null = null;
        for (const id of prev) {
          if (id === activeId) continue;
          if (id === angeheftetWirtRef.current) continue;
          if (!browserOnlyMounts.current.has(id)) continue;
          const last = mountedLastUsed.current.get(id) ?? 0;
          if (now - last > MOUNT_IDLE_TTL_MS) {
            if (!next) next = new Set(prev);
            next.delete(id);
            browserOnlyMounts.current.delete(id);
            mountedLastUsed.current.delete(id);
          }
        }
        return next ?? prev;
      });
    };
    const t = setInterval(sweep, 30_000);
    return () => clearInterval(t);
  }, [activeId]);

  // Debug: report mounted-set + render-state when the API asks. Closure sees
  // current `projects`, `mounted`, `activeId` via the render snapshot.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { correlationId?: string };
      if (!detail?.correlationId) return;
      const webviews = document.querySelectorAll('webview');
      const layoutNodeIds = Array.from(document.querySelectorAll<HTMLElement>('[data-node-id]')).map(el => el.dataset.nodeId);
      const data = {
        activeId,
        projectsCount: projects.length,
        projectIds: projects.map(p => p.id),
        mountedSize: mounted.size,
        mountedIds: Array.from(mounted),
        renderedLayoutManagers: projects.filter(p => mounted.has(p.id)).map(p => p.id),
        webviewCount: webviews.length,
        domNodeIdsSample: layoutNodeIds.slice(0, 20),
      };
      fetch('/api/control/debug/result', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ correlationId: detail.correlationId, data }),
      }).catch(err => console.error('[App] debug-result post failed', err));
    };
    window.addEventListener('debug-render-state', handler);
    return () => window.removeEventListener('debug-render-state', handler);
  }, [projects, mounted, activeId]);

  // Keyboard shortcuts: Cmd+1-9 switch projects, Cmd+N new project
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (!e.metaKey && !e.ctrlKey) return;

      // Cmd+N: new project
      if (e.key === 'n' && !e.shiftKey) {
        e.preventDefault();
        setDialogMode('create');
        setEditTarget(null);
        return;
      }

      // Cmd+0: toggle Mission Control
      if (e.key === '0') {
        e.preventDefault();
        setShowMission(prev => { if (!prev) { setShowAllChats(false);} return !prev; });
        return;
      }

      // Cmd+`: toggle All Chats
      if (e.key === '`') {
        e.preventDefault();
        setShowAllChats(prev => { if (!prev) { setShowMission(false);} return !prev; });
        return;
      }

      // Cmd+1-9: switch to project by index
      const num = parseInt(e.key);
      if (num >= 1 && num <= 9 && projects.length > 0) {
        const idx = num - 1;
        if (idx < projects.length) {
          e.preventDefault();
          setActiveId(projects[idx].id);
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [projects]);

  const handleSelect = useCallback((id: string) => {
    setActiveId(id);
  }, []);

  /**
   * Aus der Guide-Karte in den Ziel-Workspace: umschalten und die Panels freilegen,
   * die die Karte nennt.
   *
   * Die Panels werden NICHT ueber einen vorbereiteten Layout-Schreibvorgang gesetzt:
   * Layouts liegen pro Benutzer (`{id}__{uid}.json`), und der `control:apply-layout`-
   * Rundruf wird beim Empfaenger nach uid gefiltert. Eine fremde Session, die
   * Rafaels Layout schreiben wollte, traefe die geteilte Altdatei und bei ihm kaeme
   * nichts an. Deshalb legt SEIN Client die Panels frei — mit seiner Anmeldung,
   * seiner Layout-Datei. `control:ensure-panel` traegt dafuer die projectId, damit
   * die Panels nur im Ziel-Workspace entstehen und nicht in jedem geladenen.
   */
  const handleGuideTarget = useCallback((projectId: string, panels: string[] = []): boolean => {
    if (!projects.some(p => p.id === projectId)) {
      // Laut im Log, still in der Oberflaeche waere hier das Schlimmste: Rafael
      // klickt, nichts passiert, und niemand weiss warum. Deshalb `false` an den
      // Guide zurueck — der zeigt den Grund (siehe lib/buehnenWirt.ts).
      console.error(`[Guide] Karte zeigt auf Workspace "${projectId}", den es hier nicht gibt.`);
      return false;
    }
    setShowMission(false);
    setShowAllChats(false);
    setActiveId(projectId);
    if (!panels.length) return true;
    // Der Workspace kann kalt sein (Lazy-Mount). Erst wenn sein Wurzel-Element im
    // DOM steht, hoert dort ein LayoutManager zu — vorher ginge die Nachricht ins
    // Leere. Deshalb warten statt blind schicken.
    const frist = Date.now() + 8000;
    const versuchen = () => {
      if (document.querySelector(`[data-workspace-root="${projectId}"]`)) {
        for (const komponente of panels) {
          sendWs({ type: 'control:ensure-panel', component: komponente, projectId });
        }
        sendWs({ type: 'control:select-tab', target: panels[0], projectId });
        return;
      }
      if (Date.now() < frist) { setTimeout(versuchen, 200); return; }
      console.error(`[Guide] Workspace "${projectId}" ist nach 8s nicht im DOM — Panels ${panels.join(', ')} nicht freigelegt.`);
    };
    setTimeout(versuchen, 200);
    return true;
  }, [projects, sendWs]);

  /**
   * Station 3 des Guide-Wegs: die Sitzung oeffnen, in der die Arbeit haengt.
   *
   * Bewusst NICHT ueber POST /api/mission/activate: das sendet den Plan per
   * Rundruf an JEDEN verbundenen Client, also auch an ein zweites Fenster und an
   * den Schatten-Client, die Rafael gar nicht bedient. Hier wird derselbe
   * Aktivierungsplan lokal gesetzt — der LayoutManager des Ziel-Workspace nimmt ihn
   * auf, belegt einen freien Chat-Platz oder legt einen neuen an und navigiert ihn
   * auf die Sitzung.
   */
  const handleGuideSession = useCallback((projectId: string, sessionId: string, accountId: string) => {
    if (!projects.some(p => p.id === projectId)) {
      console.error(`[Guide] Sitzung ${sessionId.slice(0, 8)} zeigt auf Workspace "${projectId}", den es hier nicht gibt.`);
      return;
    }
    setShowMission(false);
    setShowAllChats(false);
    setPendingActivation([{ projectId, conversations: [{ sessionId, accountId }] }]);
    setActiveId(projectId);
  }, [projects]);

  const handleNew = useCallback(() => {
    setDialogMode('create');
    setEditTarget(null);
  }, []);

  const handleEdit = useCallback((id: string) => {
    const project = projects.find(p => p.id === id);
    if (!project) return;
    setEditTarget(project);
    setDialogMode('edit');
  }, [projects]);

  const handleDialogSubmit = useCallback(async (name: string, workDir: string) => {
    try {
      if (dialogMode === 'create') {
        const id = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        const newProject: Project = { id, name, workDir, lastOpened: new Date().toISOString() };
        setProjects(prev => [...prev, newProject]);
        setActiveId(id);
        // Save to server (auto-generates workDir for remote workspace), then re-fetch to get it
        await saveProject(newProject);
        const updated = await fetchProjects();
        setProjects(updated);
      } else if (dialogMode === 'edit' && editTarget) {
        const updated: Project = { ...editTarget, name, workDir, lastOpened: new Date().toISOString() };
        setProjects(prev => prev.map(p => p.id === editTarget.id ? updated : p));
        saveProject(updated);
      }
    } catch (err) {
      console.warn('[App] handleDialogSubmit failed:', err);
    }
    setDialogMode(null);
    setEditTarget(null);
  }, [dialogMode, editTarget]);

  const handleDelete = useCallback((id: string) => {
    setDeleteTarget(id);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteTarget) return;
    setProjects(prev => {
      const updated = prev.filter(p => p.id !== deleteTarget);
      if (deleteTarget === activeId && updated.length > 0) {
        setActiveId(updated[0].id);
      }
      return updated;
    });
    deleteProject(deleteTarget);
    setDeleteTarget(null);
  }, [deleteTarget, activeId]);

  /*
   * KOPPLUNG DER ZWEI BILDSCHIRME (Rafael, 25.09.2026) — Protokoll in lib/zweiterBildschirm.ts.
   * Bildschirm 1 reicht jede Buehne hinueber, statt sie selbst zu oeffnen; bleibt die
   * Quittung aus, oeffnet er sie doch selbst und sagt es an der Spalte.
   */
  const projectsRef = useRef(projects);
  projectsRef.current = projects;
  const handleGuideTargetRef = useRef(handleGuideTarget);
  handleGuideTargetRef.current = handleGuideTarget;
  /* Ausgefuellt weiter unten, sobald die Beenden-Wege definiert sind. */
  const zweiterWegRef = useRef<() => void>(() => {});
  const zweiter = useZweiterBildschirm({
    person: anordnungPerson,
    rolle: b2Vollbild ? 'b2' : 'b1',
    aktiv: b2Vollbild || b1Steuert,
    /*
     * Gibt die Gegenseite endgueltig auf, kehrt dieses Fenster in den Normalzustand
     * zurueck (25.09.2026, bei Kurt gemessen: Vollbild ohne Bildschirm 1 blieb fuer
     * immer, Bildschirm 1 zeigte fuer immer „getrennt"). Bildschirm 1 gibt einem
     * ladenden Fenster mehr Zeit als Bildschirm 2 einem ausbleibenden Herzschlag.
     */
    anlaufFristMs: b2Vollbild ? LEBENSZEICHEN_FRIST_MS : ANLAUF_FRIST_MS,
    beiGegenseiteWeg: () => zweiterWegRef.current(),
    // Getestet in lib/zweiterBildschirmVerdrahtung.ts (ASSIOEFFNENR R2, P2-c).
    beiAuftrag: (n: AuftragsNachricht) => b2AuftragAusfuehren(n, {
      projektBekannt: id => projectsRef.current.some(p => p.id === id),
      abdeckungRaeumen,
      buehneSetzen: setBuehnenLayout,
      arbeitsbereichAktivieren: setActiveId,
      arbeitsbereichOeffnen: (id, panels) => handleGuideTargetRef.current(id, panels),
    }),
    beiQuittung: (n: AuftragsNachricht, ok: boolean) => {
      // Nur die Quittung der Bühne, die gerade drüben stehen soll, zählt (nicht eine ältere).
      const vermerk = vermerkAusQuittung(aufB2.current, n, ok);
      if (vermerk) setB2Gezeichnet(vermerk);
    },
    beiRueckfall: (n: AuftragsNachricht) => {
      if (n.art === 'leeren') handleBuehnenLayout(null, null);
      else if (n.art === 'buehne') { aufB2.current = null; setB2Gezeichnet(null); handleBuehnenLayout(n.doc, n.projectId, n.bezug); }
      else handleGuideTargetRef.current(n.projectId, n.panels);
    },
  });
  /** Was Bildschirm 2 gerade zeigt — damit Bildschirm 1 es zurueckholen kann, wenn B2 wegfaellt. */
  const aufB2 = useRef<{ doc: unknown; projectId: string; bezug?: BuehnenBezug } | null>(null);
  /**
   * Hat Bildschirm 2 die Bühne in `aufB2` gezeichnet und quittiert? Daraus entsteht der
   * Vermerk `buehne-auf-b2`, den der Öffnungsbeleg von Bildschirm 1 liest (ASSIOEFFNENR P1).
   * Jede neue Übergabe setzt ihn zurück, bis drüben die Quittung kommt.
   */
  const [b2Gezeichnet, setB2Gezeichnet] = useState<{ bezugId?: string } | null>(null);
  const zweiterSenden = zweiter.senden;
  /* Stabil halten: der Guide fuehrt beide als Abhaengigkeit, und ein Effekt dort raeumt beim Wechsel die Buehne ab. */
  const guideBuehnenLayout = useCallback((doc: unknown | null, projectId: string | null, bezug?: BuehnenBezug) => {
    if (!b2Vollbild && zweiterSenden(doc && projectId ? { art: 'buehne', projectId, doc, ...(bezug ? { bezug } : {}) } : { art: 'leeren' })) {
      aufB2.current = doc && projectId ? { doc, projectId, bezug } : null;
      setB2Gezeichnet(null);
      setBuehnenLayout(null);
      return;
    }
    handleBuehnenLayout(doc, projectId, bezug);
  }, [b2Vollbild, zweiterSenden, handleBuehnenLayout]);
  const guideZiel = useCallback((projectId: string, panels: string[] = []): boolean => {
    if (!b2Vollbild && zweiterSenden({ art: 'arbeitsbereich', projectId, panels })) return true;
    return handleGuideTarget(projectId, panels);
  }, [b2Vollbild, zweiterSenden, handleGuideTarget]);
  const [zweiterOeffnenFehler, setZweiterOeffnenFehler] = useState<string | null>(null);
  const [zweiterWartetSeit, setZweiterWartetSeit] = useState<number | null>(null);
  useEffect(() => {
    if (zweiterWartetSeit === null) return;
    if (zweiter.gegenseite) { setZweiterWartetSeit(null); return; }
    const t = window.setTimeout(() => setZweiterWartetSeit(null), Math.max(0, zweiterWartetSeit + ANLAUF_FRIST_MS - Date.now()));
    return () => window.clearTimeout(t);
  }, [zweiterWartetSeit, zweiter.gegenseite]);
  const zweitenBildschirmOeffnen = useCallback(() => {
    const ziel = new URL(window.location.origin + window.location.pathname);
    const mode = new URLSearchParams(window.location.search).get('mode');
    if (mode) ziel.searchParams.set('mode', mode);   // Electron traegt seinen Modus in der Adresse
    ziel.searchParams.set(VOLLBILD_PARAMETER, VOLLBILD_WERT);
    const links = typeof window.screen?.availWidth === 'number' ? window.screen.availWidth : 0;
    const w = window.open(ziel.toString(), 'cui-bildschirm-2', `popup=yes,left=${links},top=0,width=1600,height=1000`);
    if (!w) {
      setZweiterOeffnenFehler('Das zweite Fenster wurde blockiert (Popup-Sperre) — bitte Pop-ups für diese Seite erlauben und erneut klicken.');
      return;
    }
    setZweiterOeffnenFehler(null);
    try { sessionStorage.setItem(STEUER_MERKER, '1'); } catch { /* gilt dann bis zum Neuladen */ }
    setB1Steuert(true);
    setZweiterWartetSeit(Date.now());
  }, []);
  const zweitenBildschirmBeenden = useCallback(() => {
    try { sessionStorage.removeItem(STEUER_MERKER); } catch { /* ignore */ }
    setB1Steuert(false);
    setZweiterWartetSeit(null);
    zweiter.hinweisWeg();
  }, [zweiter.hinweisWeg]);
  /** Kurzer, nicht blockierender Hinweis im Fenster, das das Vollbild gerade verlassen hat. */
  const [vollbildEndeHinweis, setVollbildEndeHinweis] = useState<string | null>(null);
  useEffect(() => {
    if (!vollbildEndeHinweis) return;
    const t = window.setTimeout(() => setVollbildEndeHinweis(null), 10_000);
    return () => window.clearTimeout(t);
  }, [vollbildEndeHinweis]);
  /**
   * Vollbild verlassen: Merker weg, Parameter weg, und — anders als bis 25.09.2026 —
   * die Guide-Spalte so, wie ein normaler Start sie zeigt. Vorher blieb das Fenster
   * ohne Guide und ohne Anordnungsschalter stehen; „Normale Ansicht" war keine.
   */
  const b2VollbildBeenden = useCallback((grund?: string) => {
    try { sessionStorage.removeItem(VOLLBILD_MERKER); } catch { /* ignore */ }
    const url = new URL(window.location.href);
    url.searchParams.delete(VOLLBILD_PARAMETER);
    window.history.replaceState(null, '', url.toString());
    setB2Vollbild(false);
    setShowGuide(guideAnfangsZustand({
      fernAnsicht, authEnabled, user,
      touchInput: navigator.maxTouchPoints > 0 || window.matchMedia("(pointer: coarse)").matches,
      speicher: typeof localStorage === 'undefined' ? null : localStorage,
    }));
    setVollbildEndeHinweis(grund ?? null);
  }, [fernAnsicht, authEnabled, user]);
  /** Bildschirm 1, solange Bildschirm 2 die Buehnen zeigt: die ganze Breite fuer Guide + Chat. */
  /*
   * DIE GEGENSEITE IST ENDGUELTIG WEG (25.09.2026). Bildschirm 2: das Vollbild endet
   * von selbst — auch nach einem Neuladen ohne lebenden Bildschirm 1. Bildschirm 1:
   * zurueck in den ungekoppelten Zustand, „Zweiten Bildschirm öffnen" steht wieder da;
   * ein dauerhaftes „getrennt" gibt es nicht mehr.
   */
  const zweiterHinweisSetzen = zweiter.hinweisSetzen;
  zweiterWegRef.current = () => {
    if (b2Vollbild) { b2VollbildBeenden('Bildschirm 1 ist nicht offen – normale Ansicht'); return; }
    if (!b1Steuert) return;
    try { sessionStorage.removeItem(STEUER_MERKER); } catch { /* ignore */ }
    setB1Steuert(false);
    setZweiterWartetSeit(null);
    zweiterHinweisSetzen(B2_WEG_HINWEIS);
  };
  /* Der Hinweis ist Auskunft, kein Zustand: er geht nach 15 s von selbst. */
  useEffect(() => {
    if (zweiter.hinweis !== B2_WEG_HINWEIS) return;
    const t = window.setTimeout(() => zweiterHinweisSetzen(h => (h === B2_WEG_HINWEIS ? null : h)), 15_000);
    return () => window.clearTimeout(t);
  }, [zweiter.hinweis, zweiterHinweisSetzen]);
  const b1Gekoppelt = !b2Vollbild && b1Steuert && zweiter.gekoppelt;
  const zweiterZustand: ZweiterZustand = !b1Steuert ? 'aus' : zweiter.gekoppelt ? 'gekoppelt' : zweiterWartetSeit !== null ? 'wartet' : 'getrennt';
  /*
   * Uebergaenge der Kopplung. Beginnt sie, geht eine hier schon offene Buehne mit
   * hinueber. Endet sie, kommt die zuletzt drueben gezeigte Buehne hierher zurueck —
   * sonst stuende links ihr Entscheidungsblock und rechts nichts. Endet sie, weil
   * Bildschirm 2 wegfiel (und nicht per „Beenden"), steht das an der Spalte.
   */
  const warGekoppelt = useRef(false);
  useEffect(() => {
    if (b1Gekoppelt === warGekoppelt.current) return;
    warGekoppelt.current = b1Gekoppelt;
    if (b1Gekoppelt) {
      if (buehnenLayout && zweiterSenden({ art: 'buehne', projectId: buehnenLayout.projectId, doc: buehnenLayout.doc, ...(buehnenLayout.bezug ? { bezug: buehnenLayout.bezug } : {}) })) {
        aufB2.current = buehnenLayout;
        setB2Gezeichnet(null);
        setBuehnenLayout(null);
      }
      return;
    }
    const zurueck = aufB2.current;
    aufB2.current = null;
    setB2Gezeichnet(null);
    if (!zurueck) return;
    handleBuehnenLayout(zurueck.doc, zurueck.projectId, zurueck.bezug);
    setActiveId(zurueck.projectId);
    if (b1Steuert) zweiterHinweisSetzen('Bildschirm 2 ist nicht mehr verbunden — die dort gezeigte Bühne ist wieder hier auf Bildschirm 1.');
  }, [b1Gekoppelt, b1Steuert, buehnenLayout, zweiterSenden, handleBuehnenLayout, setActiveId, zweiterHinweisSetzen]);
  const wirksamerModus = b1Gekoppelt ? 'nebeneinander' : anordnung.modus;
  /** EINE Stelle fuer die Wahl der Anordnung — Spaltenschalter und Leiste oben rufen beide hierher. */
  const anordnungWaehlen = useCallback((m: GuideAnordnungModus) => {
    anordnungAendern({ typ: 'modus', modus: m });
    // Der alte Einklapp-Schalter des Kanzlers soll der Wahl nicht widersprechen.
    setUiPreferences({ coachCollapsed: m === 'nurGuide', guideCollapsed: false, focus: false });
  }, [anordnungAendern]);
  /*
   * Von oben gewaehlt (25.09.2026): jede der vier Anordnungen lebt in der Guide-Spalte,
   * also oeffnet die Wahl sie — auch aus dem Buehnen-Vollbild heraus, das dafuer endet.
   */
  const anordnungObenWaehlen = useCallback((m: GuideAnordnungModus) => {
    if (b2Vollbild) b2VollbildBeenden();
    anordnungWaehlen(m);
    if (partnerGuide) guideBewusstSetzen(true); else setShowGuide(true);
  }, [b2Vollbild, b2VollbildBeenden, anordnungWaehlen, partnerGuide, guideBewusstSetzen]);
  const sicht = guideAnordnungSicht(wirksamerModus);
  /* Kanzlerbereich mit einem Klick ein-/ausklappen (Rafael, 03.10.2026 11:37) — Regel in kanzlerEingeklappt(). */
  const kanzlerZu = kanzlerEingeklappt(ui, wirksamerModus);
  const chatSichtbar = sicht.chat && !kanzlerZu;
  /* iPad hochkant: Guide und Buehne als zwei Reiter statt Ueberlagerung (Rafael, 03.10.2026) —
   * components/GuideBuehneReiter.tsx. Dort entscheidet der Reiter, nicht das Einklappen. */
  const ipadReiter = useGuideBuehneReiter();
  /* Reiter oder nebeneinander: ein Klick, je Geraet und Lage gemerkt (Rafael, 03.10.2026 12:50). */
  const reiterModus = showGuide && ipadReiter.ansicht === 'reiter' && !b1Gekoppelt;
  const guideZu = reiterModus ? ipadReiter.wahl !== 'guide' : guideHidden && !b1Gekoppelt;
  const buehneVerdeckt = b1Gekoppelt && !showMission && !showAllChats;
  const nebeneinander = sicht.richtung === 'zeile';
  const spaltenBreite = nebeneinander
    ? (beraterBuehneId ? Math.min(anordnung.breiteNebeneinander, 60) : anordnung.breiteNebeneinander)
    : (beraterBuehneId ? Math.min(anordnung.guideBreite, 35) : anordnung.guideBreite);
  // Die Spalte ist ab 1400 px Fensterbreite nie schmaler als 420 px (Rafael, 03.10.2026).
  // Die Grenze selbst haelt das CSS (responsive.css); hier nur, damit der Griff dort stoppt.
  const spalteMindestPx = breitesFenster ? GUIDE_SPALTE_MINDEST_PX : undefined;

  if (!loaded) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100dvh', color: 'var(--tn-text-muted)' }}>
        Loading workspace...
      </div>
    );
  }

  const deleteProjectName = deleteTarget ? projects.find(p => p.id === deleteTarget)?.name ?? deleteTarget : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Ohne Kopfleiste (Fern-Ansicht) bleibt das Banner „Neue Version“; das Abzeichen sitzt sonst in ProjectTabs. */}
      {fernAnsicht && <VersionBadge nurBanner />}
      <HintergrundBrowser />
      {b1Gekoppelt && b2Gezeichnet && <div hidden data-ai-id="buehne-auf-b2" data-buehne-id={b2Gezeichnet.bezugId ?? ''} />}
      {/* In der Fern-Ansicht gehoert dieses Band nicht hierher: es meldet die
          offenen Posten DIESES Hauses an dessen eigene Nutzer — in Rafaels
          Remote-Guide waere es die Zahl des falschen Hauses ueber seinem Guide. */}
      {/* Der Streifen „N offen" und die Sales-Zeile stehen seit 03.10.2026 als Chips in der
          Kopfleiste (ProjectTabs: onPartnerOffen / onZurueckZuSales). In der Fern-Ansicht
          gibt es keine Kopfleiste — dort fehlte der Streifen schon vorher bewusst (er haette
          die Zahl des falschen Hauses ueber Rafaels Remote-Guide gezeigt). Die Sales-Tuer
          behaelt dort ihre Zeile, damit es fuer einen Sales-Partner keinen Raum ohne Tuer gibt. */}
      {fernAnsicht && user?.uiMode === 'sales' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, padding: '5px 12px', background: 'var(--tn-bg-dark)', borderBottom: '1px solid var(--tn-border)' }}>
          <span style={{ fontSize: 12, color: 'var(--tn-text-muted)' }}>Du bist im Arbeitsbereich.</span>
          <button data-ai-id="workspace-back-to-sales" onClick={() => pickArea('sales')}
            style={{ padding: '5px 14px', borderRadius: 5, fontSize: 12, fontWeight: 700, cursor: 'pointer', background: 'var(--tn-blue)', color: '#fff', border: 'none' }}>
            ← Zurück zum Sales-Bereich
          </button>
        </div>
      )}
      {!fernAnsicht && (
      <ProjectTabs
        projects={projects}
        activeId={activeId}
        attention={projectAttention}
        missingSessions={missingSessions[activeId] || 0}
        openChatsTotal={openChatsTotal}
        openCountByProject={openCountByProject}
        onSelect={(id) => { setShowMission(false); setShowAllChats(false);handleSelect(id); }}
        onNew={handleNew}
        onEdit={handleEdit}
        onDelete={handleDelete}
        missionActive={showMission}
        onMissionClick={() => setShowMission(prev => { if (!prev) { setShowAllChats(false);} return !prev; })}
        allChatsActive={showAllChats}
        onAllChatsClick={() => setShowAllChats(prev => { if (!prev) { setShowMission(false);} return !prev; })}
        guideActive={showGuide}
        guideEingeklappt={showGuide && !reiterModus ? guideHidden : undefined}
        /*
         * EIN GUIDE, KEIN UMSCHALTER (GUIDE.md "Ein Guide, zwei Coaches, die
         * Karte waehlt das Haus"; Rafael, 09.09.2026 06:5xZ: "ich finde das
         * klicken ein bisschen dumm, wenn eh jede karte zugeordnet ist").
         * Ersetzt den fruehen GD/Remote-Guide-Umschalter (4e7427b2a) — die
         * Karte selbst bestimmt ihr Haus, nicht ein vorher gewaehlter Modus.
         */
        onGuideClick={() => {
          /* Im Buehnen-Vollbild holt „Guide" den Guide zurueck, statt nichts zu tun (25.09.2026). */
          if (b2Vollbild) { b2VollbildBeenden(); if (partnerGuide) guideBewusstSetzen(true); else setShowGuide(true); return; }
          if (partnerGuide) { guideBewusstSetzen(!showGuide); return; }
          setShowGuide(prev => !prev);
        }}
        screenId={screenId}
        /* „B1" im Buehnen-Vollbild: Vollbild beenden, dann ganz normal Bildschirm 1 (25.09.2026). */
        onScreenChange={id => { if (b2Vollbild && id === '1') b2VollbildBeenden(); setScreenId(id); }}
        onPartnerOffen={component => {
          if (component === 'group-chat') { setGruppenraumOffen(true); return; }
          // Der Guide liegt ueber dem Arbeitsbereich — ein Panel darunter waere
          // unsichtbar. Nur fuer diese Ansicht zuruecktreten (nicht als Wahl merken).
          // Dasselbe fuer Bühnen-Vollbild, Mission und Alle-Chats (wie onKalender; Zweitpruefung R1 zum Gruppenraum-Knopf).
          if (b2Vollbild) b2VollbildBeenden();
          if (showGuide) setShowGuide(false);
          setShowMission(false); setShowAllChats(false);
          setTimeout(() => window.dispatchEvent(new CustomEvent('cui-open-panel', { detail: { component } })), 0);
        }}
        onKalender={() => {
          if (b2Vollbild) b2VollbildBeenden();
          setShowGuide(false); setShowMission(false); setShowAllChats(false);
          setTimeout(() => window.dispatchEvent(new CustomEvent('cui-open-panel', { detail: { component: 'calendar' } })), 0);
        }}
        onZurueckZuSales={user?.uiMode === 'sales' ? () => pickArea('sales') : undefined}
        anordnungModus={wirksamerModus}
        anordnungFestgelegt={b1Gekoppelt}
        onAnordnung={anordnungObenWaehlen}
        kopfStatus={showGuide && zweiterZustand !== 'aus'
          ? <span data-ai-id="kopf-zweiter-bildschirm" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <ZweiterBildschirm zweiter={zweiterZustand} zweiterFehler={zweiter.kanalFehler} onZweiterOeffnen={zweitenBildschirmOeffnen} onZweiterBeenden={zweitenBildschirmBeenden} />
            </span>
          : undefined}
        menueInhalt={<>
          <button data-ai-id="project-tabs-mission-control" aria-pressed={showMission}
                  title="Übersicht: alle laufenden Sitzungen mit ihrem Zustand — arbeitet, wartet auf dich, fertig (Cmd+0)"
                  onClick={() => setShowMission(prev => { if (!prev) setShowAllChats(false); return !prev; })}>
            {showMission ? 'Übersicht schließen' : 'Übersicht'}
          </button>
          <button data-ai-id="project-tabs-all-chats" aria-pressed={showAllChats}
                  title={`Chats: alle offenen Chats an einem Ort (Cmd+\`)${openChatsTotal > 0 ? `\n${openChatsTotal} offene Chats — verschwinden erst nach Finish` : ''}`}
                  onClick={() => setShowAllChats(prev => { if (!prev) setShowMission(false); return !prev; })}>
            {showAllChats ? 'Chats schließen' : `Chats${openChatsTotal > 0 ? ` (${openChatsTotal})` : ''}`}
          </button>
          {showGuide && <>
          {zweiterZustand === 'aus' && <ZweiterBildschirm zweiter={zweiterZustand} zweiterFehler={zweiter.kanalFehler} onZweiterOeffnen={zweitenBildschirmOeffnen} onZweiterBeenden={zweitenBildschirmBeenden} />}
          <button data-ai-id="guide-standardaufteilung" title="Guide oben, Chat darunter; Guide 18 % (mind. 420 px), Kanzlerbereich 40 % der Höhe; Berater einklappen"
                  onClick={() => {
                    anordnungAendern({ typ: 'vorgabe' }); setBeraterChatOffen(false);
                    setUiPreferences({ guideCollapsed: false, coachCollapsed: null, focus: false });
                  }} >
                  Standardaufteilung
                </button>
          </>}
        </>}
      />
      )}
      {gruppenraumOffen && (
        <div data-ai-id="guide-gruppenraum-fenster" role="dialog" aria-label="Gruppenraum"
             style={{ position: 'fixed', inset: '6% 8%', zIndex: 60, display: 'flex', flexDirection: 'column',
                      background: 'var(--tn-bg)', border: '1px solid var(--tn-border)', borderRadius: 8,
                      boxShadow: '0 12px 40px rgba(0,0,0,0.45)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '4px 8px', borderBottom: '1px solid var(--tn-border)' }}>
            <button data-ai-id="guide-gruppenraum-schliessen" onClick={() => setGruppenraumOffen(false)} title="Gruppenraum schließen (Esc)"
                    style={{ background: 'none', border: '1px solid var(--tn-border)', color: 'var(--tn-text)', borderRadius: 5, padding: '2px 10px', cursor: 'pointer' }}>
              Schließen
            </button>
          </div>
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Suspense fallback={<div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--tn-text-muted)' }}>Gruppenraum lädt…</div>}>
              <GroupChatPanel />
            </Suspense>
          </div>
        </div>
      )}
      {/*
        DIE FLAECHE DES GUIDE (Rafael, 06.09.2026, an einem Entwurf abgenommen;
        Anordnung von ihm um 13:4x korrigiert)

        "Ich will das ganze CUI nicht mehr bedienen. Der Guide bedient mir das CUI."

          ┌───────────────┬─┬───────────────────────────┐
          │ Guide         │‖│                           │
          │ (Rennbahn +   │‖│  Fenster des Themas —     │
          │  Landkarte)   │‖│  so wie die zustaendige   │
          ├───────────────┤‖│  Sitzung sie in           │
          │ Coach (Chat)  │‖│  layout.kacheln gesetzt   │
          └───────────────┴─┴───────────────────────────┘

        WARUM DIE ENTSCHEIDUNGSSEITE NICHT HIER LINKS STEHT (13:4x):
        Sie stand kurz unten links unter dem Guide. Rafael: "Es macht keinen Sinn,
        die Entscheidungsgrundlage unten links reinzudruecken ... sonst zwaenge ich
        das Wichtigste am Ganzen gemeinsam mit dem Chat in ein Mini-Fenster, wo ich
        gar keinen Platz dafuer habe." Sie ist deshalb EINE KACHEL im Fenster rechts,
        neben Chat, Browser, Dateien — Groesse und Anordnung bestimmt die Sitzung,
        die das Thema vorbereitet hat. Links bleibt, was schmal sein DARF: die
        Strecke des Tages, die Themen, und darunter der Coach.

        Der Arbeitsbereich rechts ist der UNVERAENDERTE Container von unten — er
        wird nur schmaler. Panels darin (webviews, Terminals) duerfen weder neu
        mounten noch auf Breite 0 fallen; beides haette laufende Arbeit gekostet.
      */}
      {showGuide && fernAnsicht && <GuideViewControls guideHidden={guideHidden} />}
      {b2Vollbild && (
        <div data-ai-id="b2-vollbild-leiste" role="status" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '3px 10px', fontSize: 12, borderBottom: '1px solid var(--tn-border)', background: 'var(--tn-bg-dark)', color: zweiter.gegenseite ? 'var(--tn-text-muted)' : 'var(--tn-warning, var(--tn-orange))' }}>
          <span>
            {zweiter.kanalFehler
              ? `Bildschirm 2 kann nicht gekoppelt werden: ${zweiter.kanalFehler}`
              : zweiter.gegenseite
                ? 'Bildschirm 2 · Bühnen-Vollbild — jede Bühne, die du auf Bildschirm 1 anklickst, geht hier auf.'
                : 'Bildschirm 2 · Bühnen-Vollbild — Bildschirm 1 ist nicht verbunden. Dort „Zweiten Bildschirm öffnen" wählen oder das Fenster neu laden.'}
          </span>
          <span style={{ flex: 1 }} />
          <button data-ai-id="b2-vollbild-an" onClick={() => {
            document.documentElement.requestFullscreen?.().catch(e => console.error('[B2] Vollbild abgelehnt:', e));
          }} style={{ background: 'transparent', border: '1px solid var(--tn-border)', borderRadius: 4, color: 'var(--tn-text)', cursor: 'pointer', fontSize: 12 }}>⛶ Vollbild</button>
          {/* Deutlich statt klein (25.09.2026): war bei Kurt der einzige, kaum sichtbare Ausweg. */}
          <button data-ai-id="b2-vollbild-beenden" onClick={() => b2VollbildBeenden()}
            title="Bühnen-Vollbild verlassen: Guide-Spalte und Anordnung kommen zurück."
            style={{ background: 'var(--tn-blue, var(--tn-link))', border: '1px solid var(--tn-blue, var(--tn-link))', borderRadius: 4, color: 'var(--tn-bg)', cursor: 'pointer', fontSize: 13, fontWeight: 700, padding: '3px 12px' }}>
            ✕ Vollbild verlassen – normale Ansicht mit Guide
          </button>
        </div>
      )}
      {vollbildEndeHinweis && (
        <div data-ai-id="b2-vollbild-ende-hinweis" role="status" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '3px 10px', fontSize: 12, borderBottom: '1px solid var(--tn-border)', background: 'var(--tn-bg-dark)', color: 'var(--tn-warning, var(--tn-orange))' }}>
          <span style={{ flex: 1 }}>{vollbildEndeHinweis}</span>
          <button onClick={() => setVollbildEndeHinweis(null)} aria-label="Hinweis schließen" style={{ background: 'transparent', border: 0, color: 'var(--tn-text-muted)', cursor: 'pointer' }}>✕</button>
        </div>
      )}
      {reiterModus && <GuideBuehneReiter wahl={ipadReiter.wahl} onWahl={ipadReiter.setWahl} ansicht={ipadReiter.ansicht} onAnsicht={a => { if (a === 'nebeneinander') setUiPreferences({ guideCollapsed: false, focus: false }); ipadReiter.setAnsicht(a); }} />}
      <div className={`cui-work-area ${showGuide ? 'has-guide' : ''} ${guideZu ? 'guide-folded' : ''} ${ui.focus ? 'guide-focus' : ''} ${showGuide && guideBuehne && !guideZu && !b1Gekoppelt ? 'guide-overview' : ''} ${showGuide && b1Gekoppelt ? 'guide-doppel' : ''} ${reiterModus ? `ipad-reiter reiter-${ipadReiter.wahl}` : ''}`} style={{ flex: 1, display: 'flex', minHeight: 0, position: 'relative' }}>
      {/*
        HERBERTS SEITE (Rafael, 22.09.2026, mit Bild):
        "wenn er auf guide schaltet hat er links oben den guide, darunter EINEN
        (statt zwei wie bei mir) kanzler und rechts den platz fuer die buehne".

        WAS HIER VORHER STAND und warum es weg ist: ein 62 % breiter
        `PrivateHofWorkspace` — ein ANDERES Produkt als Rafaels Guide, mit
        eigenem Vize, eigenen Sandbox-Buehnen und eigenem Daemon. Am 22.09.
        unter Herberts Anmeldung gemessen: "Deine Buehnen" war leer, obwohl er
        zwei echte Buehnen im Hof hatte, und darunter stand
        `sandbox_request_failed (HTTP 410)`. Die beiden Buehnen-Begriffe haben
        nie zusammengehoert. Der private Hofstaat ist nicht geloescht, sondern
        in der Spalte verlinkt — er bleibt erreichbar, er ist nur nicht mehr
        das, was ein Partner beim Umschalten zuerst sieht.

        DIE GEOMETRIE IST ABSICHTLICH DIESELBE wie im Admin-Zweig darunter
        (`guideBreite`/`guideHoehe`, dieselben Griffe, dieselben Merker): es ist
        Rafaels Bild, nur mit einem Chat statt zweien.
      */}
      {/*
        EIN FENSTER FUER ALLE — Rafael, 22.09.2026, mit einem Bild seines eigenen
        Bildschirms: *„Warum sieht der Guide hier anders aus als bei mir ... Das sollte
        doch genau gleich ausschauen, mit diesen Entscheidungsfenstern und so weiter."*
        Und auf die Frage, wer an einer Partner-Buehne absendet: *„a — Er selbst, bei
        seinen eigenen Sachen."*

        Bis hierher stand an dieser Stelle eine Verzweigung nach Rolle: der Admin bekam
        dieses Schaltpult, ein Partner daneben eine eigene 272-Zeilen-Lesefassung ohne
        Entscheidungsfenster. Das war kein Entwurf gegen ihn, sondern die Folge davon,
        dass `/api/guide/*` hinter einem einzigen Eigentuemer-Tor lag und er gar nicht
        hindurchkam.

        Die Begrenzung sitzt jetzt dort, wo sie hingehoert — am Server
        (`partner-guide-tor.ts`), der einem Partner NUR seine eigenen Buehnen zeigt und
        jede fremde abweist. Damit ist die Rolle hier kein Kriterium mehr: gleiche
        Oberflaeche, unterschiedlicher Inhalt. `CLAUDE.md`: *„ein Rangunterschied
        zwischen Mensch und Sitzung ist nie der Grund"* — zwischen Rafael und einem
        Partner ebenso wenig.
      */}
      {showGuide && (
        <>
          <div id="guide-dock-content" className="guide-dock" inert={guideZu} aria-hidden={guideZu} style={{ flex: `0 0 ${spaltenBreite}%`, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
            {/*
              DIE EIGENE EINSTELLZEILE DER SPALTE GIBT ES NUR NOCH IN DER FERNANSICHT
              (Rafael, 03.10.2026: Kopfleisten verdichten). Sonst stehen die vier
              Anordnungen schon oben in der Leiste (ProjectTabs, 25.09.2026), und
              „Zweiten Bildschirm öffnen" und „Standardaufteilung" liegen im ⋯-Menü der
              Leiste — die Zeile kostete die Guide-Spalte eine ganze Zeile Höhe. Die
              Fernansicht (`?dgr=1`) hat keine Leiste, dort bleibt sie.
            */}
            {fernAnsicht && <div className="guide-dock-settings" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '3px 8px', borderBottom: '1px solid var(--tn-border)' }}>
              {/* Vier Anordnungen und der zweite Bildschirm (Rafael, 25.09.2026 10:22Z). */}
              <GuideAnordnungSchalter
                modus={wirksamerModus}
                festgelegt={b1Gekoppelt}
                onModus={anordnungWaehlen}
                zweiter={zweiterZustand}
                zweiterFehler={zweiter.kanalFehler}
                onZweiterOeffnen={zweitenBildschirmOeffnen}
                onZweiterBeenden={zweitenBildschirmBeenden}
              />
              <button data-ai-id="guide-standardaufteilung" title="Guide oben, Chat darunter; Guide 18 % (mind. 420 px), Kanzlerbereich 40 % der Höhe; Berater einklappen"
                onClick={() => {
                  anordnungAendern({ typ: 'vorgabe' }); setBeraterChatOffen(false);
                  setUiPreferences({ guideCollapsed: false, coachCollapsed: null, focus: false });
                }} style={{ background: 'transparent', border: 0, color: 'var(--tn-text-muted)', cursor: 'pointer', fontSize: 12 }}>
                Standardaufteilung
              </button>
            </div>}
            {[anordnungFehler, zweiterOeffnenFehler, zweiter.hinweis].filter(Boolean).map(t => (
              <div key={t} role="alert" data-ai-id="guide-anordnung-hinweis" style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '4px 8px', fontSize: 12, color: 'var(--tn-warning, var(--tn-orange))', borderBottom: '1px solid var(--tn-border)' }}>
                <span style={{ flex: 1 }}>{t}</span>
                {t === zweiter.hinweis && <button onClick={zweiter.hinweisWeg} style={{ background: 'transparent', border: 0, color: 'var(--tn-text-muted)', cursor: 'pointer' }} aria-label="Hinweis schließen">✕</button>}
                {t === zweiterOeffnenFehler && <button onClick={() => setZweiterOeffnenFehler(null)} style={{ background: 'transparent', border: 0, color: 'var(--tn-text-muted)', cursor: 'pointer' }} aria-label="Hinweis schließen">✕</button>}
              </div>
            ))}
            {/*
              DIE VIER ANORDNUNGEN. Guide und Chat bleiben in JEDER Anordnung gemountet
              (nur ausgeblendet) — ein Wechsel darf weder die offene Buehne im Guide noch
              den laufenden Chat verlieren.
            */}
            <div className="guide-inhalt" data-anordnung={wirksamerModus} style={{ flex: 1, display: 'flex', flexDirection: nebeneinander ? 'row' : 'column', minHeight: 0, minWidth: 0 }}>
            <div className="guide-top" style={{ flex: !sicht.guide ? '0 0 0' : !chatSichtbar ? '1 1 auto' : `0 0 ${nebeneinander ? anordnung.guideAnteil : anordnung.guideHoehe}%`, display: sicht.guide ? 'flex' : 'none', minHeight: 0, minWidth: 0 }}>
              <GuidePanel
                startBuehne={new URLSearchParams(location.search).get('guide-buehne')}
                assistentModus={istBetreiberGuide && ui.gespraech === 'assistent'}
                oeffnungsAuftrag={guideOeffnungsAuftrag}
                onAbdeckungRaeumen={abdeckungRaeumen}
                onOeffnenWorkspace={guideZiel}
                onOeffnenSitzung={handleGuideSession}
                onSchliessen={() => (partnerGuide ? guideBewusstSetzen(false) : setShowGuide(false))}
                onBuehne={handleGuideBuehne}
                onBuehnenLayout={guideBuehnenLayout}
                onBeraterBuehne={setBeraterStart}
                onBeraterOeffnen={() => { setShowMission(false); setShowAllChats(false); setBeraterChatOffen(true); }}
                onArbeit={handleGuideArbeit}
                breitDargestellt
              />
            </div>
            {/*
              ZWEI COACHES, NEBENEINANDER (Rafael, 09.09. — viermal verlangt:
              "bring endlich den zweiten Coach zum Laufen ... sowohl den
              Dev-Coach als auch den Partner-Coach sehen").

              Links der Coach DIESES Hauses — voll bedienbar ueber CuiLitePanel
              wie bisher. Rechts der Coach des JEWEILS ANDEREN Hauses: seine
              Sitzung laeuft auf der anderen Maschine (kein gemeinsames WS,
              kein gemeinsames JWT-Geheimnis), FremdCoachSpalte liest und
              schreibt ihn deshalb ueber die Roh-Routen in guide-coach.ts. Ein
              Etikett ("Dev"/"Partner") an jeder Spalte, damit klar ist, wohin
              man schreibt. Solange das eigene Haus keinen eingetragenen Coach
              hat, steht dort weiter der Start-Knopf.
            */}
            {/*
              DER COACH-BEREICH IST RAFAELS — Rafael, 22.09.2026, Option a:
              „Weglassen, was ihm nicht gehoert."

              Bei einem Partner stand hier „Coach-Zuordnung wird geladen …" und daneben
              „Nicht erreichbar: Insufficient permissions". Beides sind 403 auf Wege, die
              fuer ihn absichtlich zu sind — kein Defekt, aber es sieht wie einer aus.
              Der Griff darueber faellt mit weg: ohne den Bereich gibt es nichts zu teilen.
            */}
            {nebeneinander
              ? <Griff hidden={!sicht.guide || !chatSichtbar} richtung="senkrecht" wert={anordnung.guideAnteil} setzWert={v => anordnungAendern({ typ: 'groesse', feld: 'guideAnteil', wert: v })} min={GUIDE_ANORDNUNG_GRENZEN.guideAnteil.min} max={GUIDE_ANORDNUNG_GRENZEN.guideAnteil.max} merker="guide-anteil" />
              : <Griff hidden={!sicht.guide || !chatSichtbar} richtung="waagrecht" wert={anordnung.guideHoehe} setzWert={v => anordnungAendern({ typ: 'groesse', feld: 'guideHoehe', wert: v })} min={GUIDE_ANORDNUNG_GRENZEN.guideHoehe.min} max={GUIDE_ANORDNUNG_GRENZEN.guideHoehe.max} merker="guide-hoehe" />}
            <div className="guide-chat-bereich" data-zu={kanzlerZu || undefined} style={{ flex: chatSichtbar ? 1 : '0 0 auto', display: sicht.chat ? 'flex' : 'none', flexDirection: nebeneinander && !chatSichtbar ? 'row' : 'column', minHeight: 0, minWidth: 0 }}>
            {/* Ein Klick klappt Kanzler und Vizekanzler ein und aus — dasselbe Muster wie der
                Guide-Knopf und die Berater-Spalte rechts (KlappKnopf). Eingeklappt bleibt nur
                diese Leiste stehen; der Guide bekommt den Platz. */}
            {wirksamerModus !== 'nurChat' && <KlappKnopf aiId="guide-kanzler-klappen" controls={istBetreiberGuide && ui.gespraech === 'assistent' ? 'guide-assistent-content' : 'guide-coach-content'}
              offen={chatSichtbar} senkrecht={nebeneinander && !chatSichtbar}
              text={istBetreiberGuide && ui.gespraech === 'assistent' ? 'Assistent' : 'Kanzler & Vize'} pfeilOffen={nebeneinander ? '›' : '▾'} pfeilZu={nebeneinander ? '‹' : '▸'}
              onClick={() => setUiPreferences({ coachCollapsed: chatSichtbar, guideCollapsed: false, focus: false })} />}
            {/*
              EIN COACH-BEREICH FUER ALLE (Rafael, 23.09.2026 05:42Z: „Wieso muss man
              da oben eine Buehne waehlen, damit der Kanzler angezeigt wird? ... Das soll
              hier genau gleich sein wie am Dev-Server.").

              Hier stand bis dahin ein eigener Block fuer Partner, der den Chat der
              gerade gewaehlten Buehne zeigte — ohne Wahl ein leerer Platz. Das war ein
              Nachbau neben dieser Spalte. Jetzt ist es dieselbe Spalte: bei Rafael der
              Kanzler des Hauses, bei einem Partner SEIN Kanzler (Quelle oben,
              `/api/guide/mein-kanzler`). Was nur Rafael hat, weil es nur ihm gehoert:
              die Spalte des anderen Hauses und der Neustart des Hauskanzlers.
            */}
            <div className="guide-coach-tabs" hidden={!chatSichtbar || !istBetreiberGuide || ui.gespraech === 'assistent'}>
              <button aria-pressed={coachTab === 'own'} onClick={() => setCoachTab('own')}>Eigener Coach</button>
              <button aria-pressed={coachTab === 'other'} onClick={() => setCoachTab('other')}>Anderes Haus</button>
            </div>
            {<div id="guide-coach-content" inert={!chatSichtbar || (istBetreiberGuide && ui.gespraech === 'assistent')} aria-hidden={!chatSichtbar || (istBetreiberGuide && ui.gespraech === 'assistent')} className={`guide-coaches coach-${coachTab}`} style={{ flex: 1, minHeight: 0, display: chatSichtbar && (!istBetreiberGuide || ui.gespraech === 'aemter') ? 'flex' : 'none', borderTop: nebeneinander ? undefined : '1px solid var(--tn-border)', borderLeft: nebeneinander ? '1px solid var(--tn-border)' : undefined }}>
              <div style={{ flex: '1 1 50%', minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {coachFehler && <div role="alert" data-ai-id="guide-coach-ladefehler" style={{ padding: 12, color: 'var(--tn-error,var(--tn-red))' }}>
                  {coachFehler}. {coachChat ? 'Zuletzt geladener Coach bleibt sichtbar.' : 'Die Zuordnung ist derzeit nicht prüfbar.'}
                  <button data-ai-id="guide-coach-retry" disabled={!coachGeladen} onClick={() => setCoachVersuch(v => v + 1)} style={{ marginLeft: 8 }}>
                    {coachGeladen ? 'Erneut laden' : 'Lädt …'}
                  </button>
                  <div style={{ fontSize: 12 }}>Bei einer Störung wird nach 30 Sekunden erneut versucht.</div>
                </div>}
                {coachChat
                  ? <div style={{ flex: 1, minHeight: 0 }}><CoachSpalte chat={coachChat} titel={istBetreiberGuide ? `${eigenesHaus === 'partner' ? 'Partner' : eigenesHaus === 'dev' ? 'Dev' : 'Dieses Haus'} — ${coachTitel}` : coachTitel} onNeuGestartet={istBetreiberGuide ? () => { setCoachChat(null); setCoachVersuch(v => v + 1); } : undefined} ohneNeustart={!istBetreiberGuide} /></div>
                  : !coachFehler && (coachGeladen && istBetreiberGuide
                    ? <CoachStarten beiStart={(c) => { setCoachChat(c); setCoachTitel(c.label || 'Coach'); }} />
                    : <div data-ai-id="guide-coach-laedt" style={{ padding: 12, color: 'var(--tn-text-muted)' }}>{istBetreiberGuide ? 'Coach-Zuordnung wird geladen …' : 'Dein Kanzler wird geöffnet …'}</div>)}
              </div>
              {istBetreiberGuide && <div style={{ width: 1, background: 'var(--tn-border)', flexShrink: 0 }} />}
              {istBetreiberGuide && <div style={{ flex: '1 1 50%', minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <FremdCoachSpalte />
              </div>}
            </div>}
            {istBetreiberGuide && <div id="guide-assistent-content" hidden={!chatSichtbar || ui.gespraech !== 'assistent'} style={{ flex: 1, minHeight: 0, display: chatSichtbar && ui.gespraech === 'assistent' ? 'flex' : 'none', flexDirection: 'column' }}>
              <PersoenlicherAssistent aktiv={chatSichtbar && ui.gespraech === 'assistent'} />
            </div>}
            <div hidden={!chatSichtbar} style={{ flex: '0 0 auto' }}><GuideSchnellwahl /></div>
            {istBetreiberGuide && <div role="group" aria-label="Gesprächspartner" data-ai-id="guide-gespraech-umschalter" style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: 6, flexShrink: 0, borderTop: '1px solid var(--tn-border)' }}>
              {(['aemter', 'assistent'] as const).map(wahl => <button key={wahl} type="button" aria-pressed={ui.gespraech === wahl}
                data-ai-id={`guide-gespraech-${wahl}`} onClick={() => {
                  setUiPreferences({ gespraech: wahl, coachCollapsed: false, focus: false });
                  /* Der Assistent bedient die Ziele-Ansicht oben: Rafael sieht sie, sobald er ihn waehlt (Nutzer mit „Räume (bisher)“ als Vorgabe bekommen diesen Knopf nicht). */
                  if (wahl === 'assistent') guideAnsichtSteuerung.befehl({ ansicht: 'oberthema' }, 'Ziele (neu)', { stillWennGleich: true });
                }}
                style={{ flex: 1, padding: '6px 10px', borderRadius: 5, border: '1px solid var(--tn-border)', background: ui.gespraech === wahl ? 'var(--tn-bg-highlight)' : 'var(--tn-bg)', color: ui.gespraech === wahl ? 'var(--tn-blue)' : 'var(--tn-text)' }}>
                {wahl === 'aemter' ? 'Kanzler & Vize' : 'Assistent'}
              </button>)}
            </div>}
            </div>{/* guide-chat-bereich */}
            </div>{/* guide-inhalt */}
          </div>
          {nebeneinander
            ? <Griff className="guide-width-grip" richtung="senkrecht" wert={anordnung.breiteNebeneinander} setzWert={v => anordnungAendern({ typ: 'groesse', feld: 'breiteNebeneinander', wert: v })} min={GUIDE_ANORDNUNG_GRENZEN.breiteNebeneinander.min} max={GUIDE_ANORDNUNG_GRENZEN.breiteNebeneinander.max} merker="guide-breite-nebeneinander" mindestPx={spalteMindestPx} />
            : <Griff className="guide-width-grip" richtung="senkrecht" wert={anordnung.guideBreite} setzWert={v => anordnungAendern({ typ: 'groesse', feld: 'guideBreite', wert: v })} min={GUIDE_ANORDNUNG_GRENZEN.guideBreite.min} max={GUIDE_ANORDNUNG_GRENZEN.guideBreite.max} merker="guide-breite" mindestPx={spalteMindestPx} />}
        </>
      )}
      {showGuide && !reiterModus && !b1Gekoppelt && (
        /* Guide mit EINEM Klick hinaus- und hereinfahren (Rafael, 03.10.2026 11:37: „Vorbild ist die
           rechte Berater-Spalte"). Die Leiste ist ein eigenes Flex-Element: offen oder zu, sie und
           der Guide schieben die Buehne zur Seite, nichts liegt darueber. */
        <div className="guide-klappleiste" data-ai-id="guide-klappleiste">
          <KlappKnopf aiId="guide-umschalter" controls="guide-dock-content" offen={!guideZu} senkrecht
            text="Guide" pfeilOffen="‹" pfeilZu="›"
            onClick={() => setUiPreferences({ guideCollapsed: !guideZu, focus: false })} />
          <GuideAnsichtKnopf ansicht={ipadReiter.ansicht} onAnsicht={a => { if (a === 'reiter' && guideZu) setUiPreferences({ guideCollapsed: false, focus: false }); ipadReiter.setAnsicht(a); }} senkrecht />
        </div>
      )}
      <div id="guide-stage-area" className={`guide-stage-area${buehneVerdeckt ? ' guide-stage-verdeckt' : ''}`} aria-hidden={buehneVerdeckt || (reiterModus && ipadReiter.wahl === 'guide') || undefined} style={{ flex: 1, position: 'relative', minHeight: 0, minWidth: 0, isolation: 'isolate', overflow: 'hidden' }}>
        {/*
          DIE FLAECHE DES REMOTE-GUIDE

          Sie legt sich UEBER den Arbeitsbereich, statt ihn zu ersetzen: die
          Panels darunter bleiben gemountet, laufende Arbeit lauft weiter, und
          ein zweiter Klick gibt die Sicht wieder frei. Gezeigt wird NUR das
          Guide-Panel des anderen Hauses (?panel=guide) — keine zweite Kopfzeile,
          keine Arbeitsbereiche, keine Kontenleiste. In Electron ist
          es ein <webview> mit eigener Partition (eigene Anmeldung, kein
          Durchschlagen auf die Cookies hier); im Browser ein iframe, mit dem
          Ausweg "in eigenem Fenster", falls die Gegenseite Einbetten verbietet.
        */}
        {/*
          UMBAU-VORHANG (Rafael, 06.09.2026)

          "Beim Klick sollte es gleich schwarz werden und nicht erst: Uebergang,
          dann Neuladen. Obwohl die alten Sachen noch da sind, weiss ich dann
          nicht, wo ich bin."

          Das Herrichten eines Fensters braucht mehrere Schritte (Lage holen,
          Layout schreiben, Arbeitsbereich schalten, Panels mounten). Bis dahin
          stand das ALTE Fenster da und sprang dann um — der Moment, in dem er den
          Faden verliert. Der Vorhang faellt SYNCHRON im Klick, noch vor dem ersten
          fetch, und hebt sich erst, wenn die Spalte fertig ist.

          Die Notbremse ist Absicht: ein Vorhang, der haengenbleibt, waere
          schlimmer als das Springen. Nach VORHANG_MAX_MS hebt er sich von selbst
          und sagt, dass er sich selbst geoeffnet hat.
        */}
        {umbau.aktiv && (
          <div
            data-ai-id="guide-umbau-vorhang"
            style={{
              position: 'absolute', inset: 0, zIndex: 6,
              background: 'color-mix(in srgb, var(--tnfh-0f1018) 88%, transparent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column', gap: 10, pointerEvents: 'all',
              transition: 'opacity 80ms linear',
            }}
          >
            <div style={{ fontSize: 13, color: 'var(--tn-text)' }}>{umbau.was}</div>
            <div style={{ fontSize: 11, color: 'var(--tn-text-muted)' }}>wird hergerichtet …</div>
          </div>
        )}
        {/* Mission Control - global view */}
        {/*
          DIE BUEHNE DES GUIDE (Rafael, 06.09.2026)

          "Die Ansicht faehrt beim Klick auf GD von links in den bestehenden
          Workspace hinein. Das stoert mich, weil es eine eigene Sitzung ist, die
          nur den Coach betrifft — sie hat mit der Arbeit im gerade offenen
          Workspace nichts zu tun, und die Vermischung ist genau das, was der
          Guide verhindern soll."

          Solange kein Thema gewaehlt ist, bekommt der Guide deshalb die Flaeche:
          breite Spalte links, und hier rechts eine ruhige eigene Seite statt eines
          fremden Arbeitsbereichs, der nichts mit der Wahl zu tun hat.

          WARUM UEBERDECKT UND NICHT AUSGEBLENDET: `display:none` haelt die
          eingebetteten <webview>-Panels an (siehe der Kommentar an den
          Arbeitsbereichen weiter unten) — headless-Aufrufe liefen dann in
          Zeitueberschreitungen und laufende Panels waeren gestoert. Wie Mission
          Control legen wir uns darueber; darunter bleibt alles unveraendert
          stehen und laeuft weiter.
        */}
        {/*
          DER PLATZ FUER DIE BUEHNE bei einem Partner. Ueberdeckt den
          Arbeitsbereich mit demselben Kniff wie die Guide-Buehne darunter
          (absolut, statt `display:none`) — nur eben erst ab der linken Spalte,
          damit der Ueberblick und der Kanzler bedienbar bleiben.
        */}
        {showGuide && guideBuehne && !showMission && !showAllChats && (
          <div
            data-ai-id="guide-buehne"
            style={{
              position: 'absolute', inset: 0, zIndex: 2,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 8,
              background: 'var(--tn-bg-dark)', padding: 24, textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 22, opacity: 0.5 }}>🧭</div>
            <div style={{ fontSize: 13, color: 'var(--tn-text)' }}>
              {guideBuehneGrund ? 'Keine Bühne für diesen Klick.' : 'Der Guide hat das Wort.'}
            </div>
            {/*
              Der Grund kommt aus der Spalte, die den Zustand kennt (Rafael, 07.09. 09:07Z,
              Bild b0l4: "mit einer Erklaerung, warum keine Buehne angezeigt werden kann").
            */}
            <div data-ai-id="guide-buehne-grund" style={{ fontSize: 11, color: 'var(--tn-text-muted)', maxWidth: 460, lineHeight: 1.5 }}>
              {guideBuehneGrund ?? (
                /*
                  Seit dem Spalten-Umbau (11.09.2026) haengt dieser Zustand nicht mehr an
                  einem gewaehlten Thema, sondern schlicht daran, dass links noch kein
                  Berater angeklickt ist. Der alte Satz nannte einen Grund, den es nicht
                  mehr gibt — und ein Satz, der auf etwas Verschwundenes zeigt, schickt
                  die Suche in die Irre.
                */
                'Hier geht die Bühne auf, sobald du links einen Berater anklickst. Dein '
                + 'Arbeitsbereich liegt unverändert darunter und läuft weiter — der Guide '
                + 'legt sich nur darüber und räumt beim Zurückgehen wieder ab.'
              )}
            </div>
          </div>
        )}
        {showMission && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 2,
            display: 'flex', flexDirection: 'column',
          }}>
            <Suspense fallback={<div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--tn-text-muted)' }}>Loading Mission Control...</div>}>
              <MissionControl />
            </Suspense>
          </div>
        )}
        {/* All Chats - consolidated view of all active conversations */}
        {showAllChats && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 2,
            display: 'flex', flexDirection: 'column',
          }}>
            <AllChatsView
              onNavigateToProject={(projectId) => {
                setShowAllChats(false);
                setActiveId(projectId);
              }}
              isVisible={showAllChats}
            />
          </div>
        )}
        {projects.filter(p => mounted.has(p.id)).map((p) => (
          <div
            key={p.id}
            // Marks the workspace root so the screenshot path can find the owning
            // workspace of a panel and force it painted across a capture (Electron
            // does not composite a <webview> under visibility:hidden).
            data-workspace-root={p.id}
            // visibility:hidden + pointer-events:none + opacity:0 keeps the
            // subtree mounted and laid out (so Electron <webview> elements
            // boot their renderer processes) but invisible and inert. Using
            // display:none here would pause the embedded webviews — then
            // headless /eval/click/type calls timeout on background workspaces.
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: p.id === activeId ? 1 : -1,
              visibility: p.id === activeId ? 'visible' : 'hidden',
              opacity: p.id === activeId ? 1 : 0,
              pointerEvents: p.id === activeId ? 'auto' : 'none',
              display: 'flex',
              flexDirection: 'column',
              // Cancel the ancestor zoom here — LayoutManager re-applies it per
              // panel (wrapPanel) and exempts the Browser panel specifically.
              zoom: 1,
            }}
          >
              <LayoutManager
                key={`${p.id}--${screenId}`}
                projectId={p.id}
                layoutKey={screenId !== '1' ? `${p.id}--s${screenId}` : undefined}
                workDir={p.workDir}
                pendingActivation={pendingActivation}
                isActive={p.id === activeId}
                onActivationProcessed={handleActivationProcessed}
                onMissingSessions={(count) => handleMissingSessions(p.id, count)}
                /*
                  Zweite Sicherung: die Buehne gehoert zum Guide. Ist die Spalte zu,
                  wird sie nie gezeigt — auch dann nicht, wenn ein Dokument aus einem
                  frueheren Klick noch im Zustand steht. Ein Ueberlagerer, den niemand
                  wegklicken kann, waere schlimmer als gar keiner.
                */
                /*
                  Dritte Sicherung: sagt die Spalte "keine Buehne" (guideBuehne), gibt es auch
                  keine. Erklaerflaeche und liegengebliebenes Dokument duerfen nie beide stehen —
                  beide liegen mit z-index ueber dem Arbeitsbereich, und das spaetere im DOM
                  gewinnt (Bild b0l4, 07.09.: Energy-Buehne unter "Privat wird vorbereitet").
                */
                buehnenBezug={buehnenLayout?.bezug}
                buehne={((showGuide && !guideBuehne) || b2Vollbild) && buehnenLayout && buehnenWirtId === p.id ? buehnenLayout.doc : null}
                angeheftet={angeheftet && angeheftetWirtId === p.id ? angeheftet : null}
                onAnheften={istBetreiberGuide ? handleAnheften : undefined}
              />
          </div>
        ))}
      </div>
      {showGuide && beraterBuehneId && !showMission && !showAllChats && (
        <GuideBeraterSidebar key={beraterBuehneId} buehneId={beraterBuehneId} start={beraterStart ?? undefined} offen={!ui.focus && beraterChatOffen} onOffenChange={value => { setUiPreferences({ focus: false }); setBeraterChatOffen(value); }} />
      )}
      </div>

      {dialogMode && (
        <ProjectDialog
          mode={dialogMode}
          initialName={editTarget?.name}
          initialWorkDir={editTarget?.workDir}
          onSubmit={handleDialogSubmit}
          onClose={() => { setDialogMode(null); setEditTarget(null); }}
        />
      )}

      {deleteTarget && (
        <DeleteDialog
          projectName={deleteProjectName}
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}


/**
 * DER KNOPF, DER DEN COACH DIESES HAUSES OEFFNET.
 *
 * Absichtlich karg: ein Satz, warum hier nichts steht, und ein Knopf. Der Server
 * entscheidet, ob es geht (Rollenblatt vorhanden) und sagt sonst den Grund —
 * ein Knopf, der stumm nichts tut, waere die naechste Fehlersuche.
 */
function CoachStarten({ beiStart }: { beiStart: (c: { sessionId: string; accountId?: string; label?: string }) => void }) {
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const starten = async () => {
    setLaeuft(true); setFehler(null);
    try {
      const r = await fetch('/api/guide/coach/starten', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.error || `HTTP ${r.status}`);
      beiStart({ sessionId: d.sessionId, accountId: d.accountId, label: 'Coach' });
    } catch (e: any) {
      setFehler(String(e?.message || e));
    } finally {
      setLaeuft(false);
    }
  };
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 16, textAlign: 'center' }}>
      <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', maxWidth: 340, lineHeight: 1.5 }}>
        In diesem Haus laeuft kein Coach. Er ist eine eigene Sitzung hier — der Coach des anderen Hauses
        laesst sich von hier aus nicht bedienen.
      </div>
      <button
        data-ai-id="coach-starten"
        onClick={starten}
        disabled={laeuft}
        style={{
          background: 'none', border: '1px solid var(--tn-border)', borderRadius: 6,
          color: laeuft ? 'var(--tn-text-muted)' : 'var(--tn-orange)', fontSize: 12, padding: '6px 14px',
          cursor: laeuft ? 'default' : 'pointer',
        }}
      >
        {laeuft ? 'Coach wird geoeffnet …' : 'Coach starten'}
      </button>
      {fehler && <div style={{ fontSize: 11, color: 'var(--tn-red)', maxWidth: 340 }}>{fehler}</div>}
    </div>
  );
}
